import {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  type MutableRefObject,
} from "react";
import {
  MapPin,
  Users,
  DollarSign,
  Coins,
  X,
  Search,
  SlidersHorizontal,
  LayoutGrid,
  CircleDashed,
  Hash,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  Sparkles,
  Link2,
  ChevronRight,
  Maximize2,
  AlertTriangle,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  SearchAutocomplete,
  type SearchAutocompleteHandle,
} from "@/components/inputs/search-autocomplete/search-autocomplete";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSearchSpaces } from "@/hooks/useSearchSpaces";
import { spaceService } from "@/services/space-service";
import { privateOfficeService } from "@/services/private-office-service";
import type { SearchParams } from "@/services/private-office-service/types";
import { useFilters } from "@/hooks/use-filters";
import { SearchInputCommand } from "@/components/inputs/search-input-command";
import { useAuth } from "@/features/authentication";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { visionService } from "@/services/vision-service";
import { reportError } from "@/utils/observability";
import { SpaceDetailDrawer } from "../components/SpaceDetailDrawer";
import { SpaceCard } from "../components/SpaceCard";
import { AISearchLoader } from "../components/AISearchLoader";
import { AISearchError } from "../components/AISearchError";
import {
  useSearchAndRank,
  buildSearchModeParams,
} from "../hooks/useSearchAndRank";
import type {
  SearchModeState,
  RoomCombination,
} from "../hooks/useSearchAndRank";
import type {
  LocationData,
  LivePreview,
  ActiveCombinationPreview,
  RankedSpace,
  DiagnosisData,
  SearchSource,
  SpaceDiff,
} from "../types";
import { FIXED_CREDIT_PRICE } from "../../pricing/marginCalculator";
import { mapSearchResultToRankedSpaces } from "@/services/vision-service/mappers/filters-to-ranked-space";
import { computeSpaceDiff } from "../utils/computeSpaceDiff";
import { withFreshComplianceFlags } from "../utils/complianceFlags";
import { isDuplicateAddById } from "../utils/isDuplicateAddById";
import { deriveGroupIdsByRoom, applyGroupIds } from "../utils/comboGrouping";
import { SpaceDataDiffModal } from "../components/SpaceDataDiffModal";
import {
  ScoreDetailsModal,
  type ScoreAnalysis,
} from "@/features/vision-360/components/score-details-modal";

const RADIUS_OPTIONS = [
  { value: 3, label: "3 km" },
  { value: 5, label: "5 km" },
  { value: 30, label: "30 km" },
] as const;

const PRESET_RADII = RADIUS_OPTIONS.map((o) => o.value);

const CAP_PRICE_TOL_OPTIONS = [
  { label: "Exata", value: 0.0 },
  { label: "20%", value: 0.2 },
  { label: "30%", value: 0.3 },
] as const;

function snapCapPriceTol(v?: number | null): number {
  return v ?? 0.0;
}

function initCapPriceCustomStr(v?: number | null): string {
  if (v == null || v === 0) return "";
  const isPreset = CAP_PRICE_TOL_OPTIONS.some(
    (o) => Math.abs(o.value - v) < 0.001,
  );
  return isPreset ? "" : String(Math.round(v * 100));
}

interface LocationStepProps {
  data: LocationData | null;
  diagnosis?: DiagnosisData | null;
  onNext: (data: LocationData) => void;
  onPreviewUpdate?: (data: Partial<LivePreview>) => void;
  onDiagnosisSync?: (data: {
    positionsMin: number | "";
    positionsMax: number | "";
    targetCity: string;
    selectedLocation: { label: string; lat: number; lng: number } | null;
    includeUnavailableWithinPeriod?: number;
  }) => void;
  onValidityChange?: (valid: boolean) => void;
  registerNext?: (fn: () => void) => void;
  /** Ref para o wizard persistir o estado do formulário ao clicar em Voltar */
  getFormStateRef?: MutableRefObject<(() => Partial<LocationData>) | null>;
  /** Callback registrado pelo wizard para receber cliques em marcadores do mapa */
  registerOnMarkerClick?: (fn: (id: string) => void) => void;
  /** Salas originais da proposta em edição — exibidas em seção separada acima dos resultados */
  originalSpaces?: RankedSpace[];
  /** Busca rápida (PED-12650): banner fixo + tracking de uso, somente consulta. */
  isQuickMode?: boolean;
}

export default function LocationStep({
  data,
  diagnosis,
  onNext,
  onPreviewUpdate,
  onDiagnosisSync,
  onValidityChange,
  registerNext,
  getFormStateRef,
  registerOnMarkerClick,
  originalSpaces,
  isQuickMode = false,
}: LocationStepProps) {
  const [location, setLocation] = useState<LocationData["location"]>(() => {
    if (data?.location) return data.location;
    if (diagnosis?.selectedLocation) {
      return {
        label: diagnosis.targetCity?.trim() || diagnosis.selectedLocation.label,
        lat: diagnosis.selectedLocation.lat,
        lng: diagnosis.selectedLocation.lng,
      };
    }
    const labelFromDiagnosis =
      diagnosis?.targetCity?.trim() ||
      diagnosis?.importedData?.hard_constraints?.city ||
      "";
    if (!labelFromDiagnosis) return null;
    return {
      label: labelFromDiagnosis,
      lat: 0,
      lng: 0,
    };
  });
  const [locationInputValue, setLocationInputValue] = useState(() => {
    if (data?.location?.label) return data.location.label;
    if (diagnosis?.selectedLocation)
      return diagnosis.targetCity?.trim() || diagnosis.selectedLocation.label;
    return (
      diagnosis?.targetCity?.trim() ||
      diagnosis?.importedData?.hard_constraints?.city ||
      ""
    );
  });
  const [radiusKm, setRadiusKm] = useState<number | null>(() => {
    const saved = data?.radiusKm;
    return saved != null ? saved : 5;
  });
  const [customRadiusMode, setCustomRadiusMode] = useState(() => {
    const saved = data?.radiusKm;
    return (
      saved != null && !(PRESET_RADII as readonly number[]).includes(saved)
    );
  });
  const [customRadiusInput, setCustomRadiusInput] = useState(() => {
    const saved = data?.radiusKm;
    return saved != null && !(PRESET_RADII as readonly number[]).includes(saved)
      ? String(saved)
      : "";
  });
  const [selectedSpaceIds, setSelectedSpaceIds] = useState<string[]>(
    data?.selectedSpaceIds ?? [],
  );
  // Combos are a separate selection dimension from individual rooms — a room can
  // be picked individually AND belong to a chosen combo. Restored proposals carry
  // selectedComboIds; older ones fall back to the combos rebuilt from groupIds.
  const [selectedComboIds, setSelectedComboIds] = useState<string[]>(
    () =>
      data?.selectedComboIds ??
      data?.roomCombinations
        ?.filter((c) =>
          c.rooms.some((r) =>
            (data?.rankedSpaces ?? []).some(
              (s) => s.id === r.privateRoomId && (s.groupIds?.length ?? 0) > 0,
            ),
          ),
        )
        .map((c) => c.combinationId) ??
      [],
  );
  const [idAddedSpaces, setIdAddedSpaces] = useState<RankedSpace[]>(
    data?.idAddedSpaces ?? [],
  );
  const [addByIdOpen, setAddByIdOpen] = useState(false);
  const [addByIdValue, setAddByIdValue] = useState("");
  const [addByIdStatus, setAddByIdStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [addByIdError, setAddByIdError] = useState<string | null>(null);
  const [addByIdFoundSpace, setAddByIdFoundSpace] =
    useState<RankedSpace | null>(null);
  const [spaceDiffs, setSpaceDiffs] = useState<Map<string, SpaceDiff>>(
    new Map(),
  );
  const [updatedOriginalSpaces, setUpdatedOriginalSpaces] = useState<
    Map<string, RankedSpace>
  >(new Map());
  const [diffModalTarget, setDiffModalTarget] = useState<SpaceDiff | null>(
    null,
  );
  const [drawerSpace, setDrawerSpace] = useState<RankedSpace | null>(null);
  const [comboScoreModal, setComboScoreModal] = useState<{
    criteria: ScoreAnalysis[];
    score: number;
  } | null>(null);
  const [activeSpaceId, setActiveSpaceId] = useState<string | null>(null);
  const [showOtherSpaces] = useState(false); // kept for preview compatibility
  const [activeTab, setActiveTab] = useState<
    "recommended" | "other" | "combinations"
  >("recommended");
  const [showOutdatedSpaces, setShowOutdatedSpaces] = useState(false);
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);
  const [locationMode, setLocationMode] = useState<
    "location" | "neighborhoods"
  >(data?.locationMode ?? "location");
  const [neighCity, setNeighCity] = useState<{
    id: number;
    name: string;
  } | null>(data?.cityName ? { id: 0, name: data.cityName } : null);
  const [neighSelectedNeighborhoods, setNeighSelectedNeighborhoods] = useState<
    { id: number; name: string }[]
  >((data?.neighborhoodNames ?? []).map((n, i) => ({ id: i, name: n })));

  const [capacityMin, setCapacityMin] = useState<string>(() => {
    if (data?.capacityMin != null) return String(data.capacityMin);
    if (diagnosis?.positionsMin !== undefined && diagnosis.positionsMin !== "")
      return String(diagnosis.positionsMin);
    return "";
  });
  const [capacityMax, setCapacityMax] = useState<string>(() => {
    if (data?.capacityMax != null) return String(data.capacityMax);
    if (diagnosis?.positionsMax !== undefined && diagnosis.positionsMax !== "")
      return String(diagnosis.positionsMax);
    return "";
  });
  const [priceMin, setPriceMin] = useState<string>(() => {
    if (data !== null)
      return data?.priceMin != null ? String(data.priceMin) : "";
    const imported = diagnosis?.importedData?.hard_constraints?.min_budget;
    if (imported != null) return String(imported);
    return "";
  });
  const [priceMax, setPriceMax] = useState<string>(() => {
    if (data !== null)
      return data?.priceMax != null ? String(data.priceMax) : "";
    const imported = diagnosis?.importedData?.hard_constraints?.max_budget;
    if (imported != null) return String(imported);
    if (diagnosis?.monthlyBudget) {
      const parsed = Number(String(diagnosis.monthlyBudget).replace(/\D/g, ""));
      if (parsed > 0) return String(parsed);
    }
    return "";
  });
  const [unavailableDaysEnabled, setUnavailableDaysEnabled] = useState<boolean>(
    () => {
      const v =
        data?.includeUnavailableWithinPeriod ??
        diagnosis?.includeUnavailableWithinPeriod;
      return (v ?? 0) > 0;
    },
  );
  const [unavailableDaysValue, setUnavailableDaysValue] = useState<string>(
    () => {
      const v =
        data?.includeUnavailableWithinPeriod ??
        diagnosis?.includeUnavailableWithinPeriod;
      return v != null ? String(v) : "30";
    },
  );

  const includeUnavailableWithinPeriod: number | undefined =
    unavailableDaysEnabled && unavailableDaysValue
      ? Number(unavailableDaysValue) || undefined
      : undefined;

  const includeUnavailableWithinPeriodRef = useRef<number | undefined>(
    includeUnavailableWithinPeriod,
  );
  includeUnavailableWithinPeriodRef.current = includeUnavailableWithinPeriod;

  const [tolCapIgnored, setTolCapIgnored] = useState<boolean>(
    data?.toleranceCapacityIgnored ?? false,
  );
  const [tolCapValue, setTolCapValue] = useState<number>(() =>
    snapCapPriceTol(data?.toleranceCapacity),
  );
  const [tolCapCustomStr, setTolCapCustomStr] = useState<string>(() =>
    initCapPriceCustomStr(data?.toleranceCapacity),
  );
  const [tolPriceIgnored, setTolPriceIgnored] = useState<boolean>(
    data?.tolerancePriceIgnored ?? false,
  );
  const [tolPriceValue, setTolPriceValue] = useState<number>(() =>
    snapCapPriceTol(data?.tolerancePrice),
  );
  const [tolPriceCustomStr, setTolPriceCustomStr] = useState<string>(() =>
    initCapPriceCustomStr(data?.tolerancePrice),
  );
  const [tolDistIgnored, setTolDistIgnored] = useState<boolean>(
    data?.toleranceDistanceIgnored ?? false,
  );
  const [tolDistKm, setTolDistKm] = useState<string>(
    data?.toleranceDistanceKm != null ? String(data.toleranceDistanceKm) : "",
  );

  const toleranceForSearch = useCallback(() => {
    const distKm = parseFloat(tolDistKm);
    const r = radiusKm ?? 5;
    return {
      capacity: tolCapIgnored ? 1.0 : tolCapValue,
      price: tolPriceIgnored ? 1.0 : tolPriceValue,
      distance: tolDistIgnored ? 1.0 : distKm > 0 && r > 0 ? distKm / r : 0.0,
    };
  }, [
    tolCapIgnored,
    tolCapValue,
    tolPriceIgnored,
    tolPriceValue,
    tolDistIgnored,
    tolDistKm,
    radiusKm,
  ]);

  const buildCurrentSearchModeParams = useCallback(
    (overrides?: { radiusKm?: number | null; neighborhoods?: string[] }) => {
      const isNeighborhoods = locationMode === "neighborhoods";
      const state: SearchModeState = {
        mode: isNeighborhoods ? "neighborhood" : "location",
        ...(isNeighborhoods
          ? {
              neighborhoods:
                overrides?.neighborhoods ??
                neighSelectedNeighborhoods.map((n) => n.name),
              cityName: neighCity?.name,
            }
          : {
              location: {
                latitude: location?.lat ?? 0,
                longitude: location?.lng ?? 0,
                radius: overrides?.radiusKm ?? radiusKm ?? 30,
              },
            }),
        minCapacity: capacityMin !== "" ? Number(capacityMin) : null,
        maxCapacity: capacityMax !== "" ? Number(capacityMax) : null,
        minPrice: priceMin !== "" ? Number(priceMin) : null,
        maxPrice: priceMax !== "" ? Number(priceMax) : null,
        unitCreditPrice: FIXED_CREDIT_PRICE,
        includeUnavailableWithinPeriod:
          includeUnavailableWithinPeriodRef.current,
      };
      return buildSearchModeParams(state, toleranceForSearch());
    },
    [
      locationMode,
      neighSelectedNeighborhoods,
      neighCity,
      location,
      radiusKm,
      capacityMin,
      capacityMax,
      priceMin,
      priceMax,
      toleranceForSearch,
    ],
  );

  const { userInfo } = useAuth();
  const { spaces, handleSearchSpaces, isLoading } = useSearchSpaces();
  const {
    handSelectedCity: filterHandSelectedCity,
    filterCities,
    handSelectedNeighborhood: filterHandSelectedNeighborhood,
    filterNeighborhoods,
  } = useFilters();
  const locationRef = useRef<SearchAutocompleteHandle>(null);
  const {
    spaces: rankedSpaces,
    outdatedSpaces,
    status: rankStatus,
    recommendationHash,
    searchMode,
    roomCombinations,
    extraSpaces,
  } = useSearchAndRank();

  const effectiveSearchSource: SearchSource = "search";

  const hasRestoredResults = !!(
    data?.rankedSpaces?.length && rankStatus === "idle"
  );
  const displaySpaces = hasRestoredResults ? data!.rankedSpaces! : rankedSpaces;
  const displayRecommendationHash = hasRestoredResults
    ? (data!.recommendationHash ?? null)
    : recommendationHash;
  const displayRankStatus = hasRestoredResults ? "success" : rankStatus;
  // Combinations: prefer live results; fall back to saved state when restored
  const displayCombinations = useMemo(
    () =>
      roomCombinations.length > 0
        ? roomCombinations
        : hasRestoredResults
          ? (data?.roomCombinations ?? [])
          : [],
    [roomCombinations, hasRestoredResults, data?.roomCombinations],
  );

  // IDs das salas originais da proposta — usados para excluí-las dos resultados de busca
  const originalSpaceIds = useMemo(
    () => new Set(originalSpaces?.map((s) => s.id) ?? []),
    [originalSpaces],
  );

  const idAddedSpaceIds = useMemo(
    () => new Set(idAddedSpaces.map((s) => s.id)),
    [idAddedSpaces],
  );

  const addedByIdInputs = useMemo(
    () =>
      new Set(
        idAddedSpaces
          .map((s) => s.addedByIdValue)
          .filter((v): v is string => Boolean(v)),
      ),
    [idAddedSpaces],
  );

  // Resultados da busca excluindo salas já na proposta e salas adicionadas por ID
  const filteredDisplaySpaces = useMemo(
    () =>
      displaySpaces.filter(
        (s) => !originalSpaceIds.has(s.id) && !idAddedSpaceIds.has(s.id),
      ),
    [displaySpaces, originalSpaceIds, idAddedSpaceIds],
  );

  const filteredOutdatedSpaces = useMemo(
    () =>
      outdatedSpaces.filter(
        (s) => !originalSpaceIds.has(s.id) && !idAddedSpaceIds.has(s.id),
      ),
    [outdatedSpaces, originalSpaceIds, idAddedSpaceIds],
  );

  const matchedSpaces = useMemo(
    () => filteredDisplaySpaces.filter((s) => s.score >= 80),
    [filteredDisplaySpaces],
  );
  const otherSpaces = useMemo(
    () => filteredDisplaySpaces.filter((s) => s.score < 80),
    [filteredDisplaySpaces],
  );

  const noMatchTrackedRef = useRef(false);
  useEffect(() => {
    const hasNoMatch =
      displayRankStatus === "success" &&
      matchedSpaces.length === 0 &&
      otherSpaces.length > 0;
    if (hasNoMatch && !noMatchTrackedRef.current) {
      noMatchTrackedRef.current = true;
      sendTrackDataServer({
        eventName: "VISION_AI_MODE_LOCATION_SEARCH_NO_MATCH",
        productType: "PO",
        url: window.location.pathname,
        data: {
          deal_id: diagnosis?.dealId || null,
          user_id: userInfo?.user?.id ?? null,
          email: userInfo?.profile?.email ?? null,
          results_count: otherSpaces.length,
          score_threshold: 80,
          filters: {
            location_label: location?.label ?? null,
            radius_km: radiusKm,
            capacity_min: capacityMin ? Number(capacityMin) : null,
            capacity_max: capacityMax ? Number(capacityMax) : null,
            credits_value: FIXED_CREDIT_PRICE,
            price_min: priceMin ? Number(priceMin) : null,
            price_max: priceMax ? Number(priceMax) : null,
            location_mode: locationMode,
            neighborhood_names:
              locationMode === "neighborhoods"
                ? neighSelectedNeighborhoods.map((n) => n.name)
                : null,
          },
        },
      });
    } else if (!hasNoMatch) {
      noMatchTrackedRef.current = false;
    }
  }, [
    displayRankStatus,
    matchedSpaces.length,
    otherSpaces.length,
    diagnosis?.dealId,
    userInfo?.user?.id,
    userInfo?.profile?.email,
    location?.label,
    radiusKm,
    capacityMin,
    capacityMax,
    priceMin,
    priceMax,
    locationMode,
    neighSelectedNeighborhoods,
  ]);

  const lastSearchParamsRef = useRef<SearchParams | null>(null);
  const searchStartTimeRef = useRef<number | null>(null);
  const prevRankStatusRef = useRef(rankStatus);
  const hasInitialSearchFiredRef = useRef(false);

  // groupIds come from which COMBOS are selected (selectedComboIds) — independent
  // of individual room selection. A room can be in several selected combos.
  const groupIdsByRoom = useMemo(
    () => deriveGroupIdsByRoom(displayCombinations, selectedComboIds),
    [displayCombinations, selectedComboIds],
  );

  // Rooms that end up in the proposal = individually-selected rooms ∪ rooms of the
  // selected combos. Used to gate validity and (downstream) which spaces are saved.
  const proposalRoomIds = useMemo(() => {
    const ids = new Set(selectedSpaceIds);
    for (const combo of displayCombinations) {
      if (!selectedComboIds.includes(combo.combinationId)) continue;
      for (const r of combo.rooms) ids.add(r.privateRoomId);
    }
    return ids;
  }, [selectedSpaceIds, selectedComboIds, displayCombinations]);

  // Stamp each candidate space with its combo memberships (groupIds) and whether
  // it was picked individually (isIndividual) — both persist so every consumer can
  // render a room as a standalone and/or inside its combo(s).
  const stampSelection = useCallback(
    <T extends { id: string; groupIds?: string[]; isIndividual?: boolean }>(
      spaces: T[],
    ): T[] =>
      applyGroupIds(spaces, groupIdsByRoom).map((s) => ({
        ...s,
        isIndividual: selectedSpaceIds.includes(s.id),
      })),
    [groupIdsByRoom, selectedSpaceIds],
  );

  // Combina salas originais + adicionadas por ID + resultados da busca,
  // carimbando groupIds + isIndividual.
  const allRankedSpacesForSave = useMemo(
    () =>
      stampSelection([
        ...(originalSpaces ?? []).map(
          (s) => updatedOriginalSpaces.get(s.id) ?? s,
        ),
        ...idAddedSpaces,
        ...filteredDisplaySpaces,
      ]),
    [
      originalSpaces,
      updatedOriginalSpaces,
      idAddedSpaces,
      filteredDisplaySpaces,
      stampSelection,
    ],
  );

  // Extra rooms from combos: prefer live data, fall back to saved state on restore.
  // Combo rooms can live in extraSpaces (the backend's `extra_rooms`), not just in
  // allRankedSpacesForSave, so they need the same grouping/individual marks.
  const displayExtraSpaces = useMemo(() => {
    const base =
      extraSpaces.length > 0 ? extraSpaces : (data?.extraSpaces ?? []);
    return stampSelection(base);
  }, [extraSpaces, data?.extraSpaces, stampSelection]);

  useEffect(() => {
    if (!getFormStateRef) return;
    getFormStateRef.current = () => ({
      location,
      radiusKm,
      capacityMin: capacityMin !== "" ? Number(capacityMin) : null,
      capacityMax: capacityMax !== "" ? Number(capacityMax) : null,
      creditsPrice: FIXED_CREDIT_PRICE,
      selectedSpaceIds,
      selectedComboIds,
      rankedSpaces: allRankedSpacesForSave,
      extraSpaces:
        displayExtraSpaces.length > 0
          ? displayExtraSpaces
          : (data?.extraSpaces ?? []),
      idAddedSpaces,
      recommendationHash: displayRecommendationHash,
      searchSource: effectiveSearchSource,
      locationMode,
      neighborhoodNames: neighSelectedNeighborhoods.map((n) => n.name),
      includeUnavailableWithinPeriod: includeUnavailableWithinPeriodRef.current,
      toleranceCapacity: tolCapIgnored ? 1.0 : tolCapValue,
      toleranceCapacityIgnored: tolCapIgnored,
      tolerancePrice: tolPriceIgnored ? 1.0 : tolPriceValue,
      tolerancePriceIgnored: tolPriceIgnored,
      toleranceDistance: toleranceForSearch().distance,
      toleranceDistanceIgnored: tolDistIgnored,
      toleranceDistanceKm: tolDistKm ? parseFloat(tolDistKm) : null,
      priceMin: priceMin !== "" ? Number(priceMin) : null,
      priceMax: priceMax !== "" ? Number(priceMax) : null,
      roomCombinations:
        roomCombinations.length > 0
          ? roomCombinations
          : (data?.roomCombinations ?? []),
    });
    return () => {
      getFormStateRef.current = null;
    };
  }, [
    getFormStateRef,
    location,
    radiusKm,
    capacityMin,
    capacityMax,
    priceMin,
    priceMax,
    selectedSpaceIds,
    selectedComboIds,
    allRankedSpacesForSave,
    idAddedSpaces,
    displayRecommendationHash,
    effectiveSearchSource,
    locationMode,
    neighSelectedNeighborhoods,
    tolCapIgnored,
    tolCapValue,
    tolPriceIgnored,
    tolPriceValue,
    tolDistIgnored,
    tolDistKm,
    toleranceForSearch,
    displayExtraSpaces,
    roomCombinations,
    data?.roomCombinations,
    data?.extraSpaces,
  ]);

  useEffect(() => {
    if (hasInitialSearchFiredRef.current) return;
    hasInitialSearchFiredRef.current = true;
    // Em modo de edição (originalSpaces presente) sempre roda a busca inicial,
    // pois data.rankedSpaces contém apenas as salas selecionadas da proposta —
    // os resultados frescos precisam aparecer na seção abaixo.
    if (data?.rankedSpaces?.length && !originalSpaces?.length) {
      return;
    }
    if (diagnosis) {
      const hasValidCoords =
        location != null &&
        location.lat != null &&
        location.lng != null &&
        !(location.lat === 0 && location.lng === 0);
      // In location mode, defer the initial auto-search until a real point is
      // chosen — otherwise it fires with {0,0} (the Gulf of Guinea) and returns
      // wrong/empty results. In neighborhoods mode, require at least one selected
      // neighborhood (a restored/migrated proposal may have none). Either way the
      // user's "Buscar" click fires it once the anchor is set.
      const hasNeighborhoods =
        locationMode === "neighborhoods" &&
        neighSelectedNeighborhoods.length > 0;
      if (hasNeighborhoods || hasValidCoords) {
        const params = buildCurrentSearchModeParams();
        lastSearchParamsRef.current = params;
        searchStartTimeRef.current = Date.now();
        searchMode(params);
      }
    }
    if (
      !data?.location &&
      !diagnosis?.selectedLocation &&
      diagnosis?.targetCity
    ) {
      handleSearchSpaces(diagnosis.targetCity);
      setTimeout(() => locationRef.current?.focus(), 50);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (rankStatus === "loading") {
      setActiveSpaceId(null);
      onPreviewUpdate?.({ rankedSpaces: [], activeSpaceId: null });
    } else if (rankStatus === "success" || hasRestoredResults) {
      const spaces = hasRestoredResults
        ? (data?.rankedSpaces ?? [])
        : rankedSpaces;
      const firstId = spaces.length > 0 ? spaces[0].id : null;
      setActiveSpaceId(firstId);
      onPreviewUpdate?.({ activeSpaceId: firstId });
    }
  }, [
    rankStatus,
    rankedSpaces,
    hasRestoredResults,
    data?.rankedSpaces,
    onPreviewUpdate,
  ]);

  // Mantém preview.rankedSpaces e extraSpaces sincronizados
  useEffect(() => {
    onPreviewUpdate?.({
      rankedSpaces: allRankedSpacesForSave,
      extraSpaces: displayExtraSpaces,
    });
  }, [allRankedSpacesForSave, displayExtraSpaces, onPreviewUpdate]);

  useEffect(() => {
    if (displaySpaces.length > 0 && activeSpaceId === null) {
      const firstId = displaySpaces[0].id;
      setActiveSpaceId(firstId);
      onPreviewUpdate?.({ activeSpaceId: firstId });
    }
  }, [displaySpaces, activeSpaceId, onPreviewUpdate]);

  // Auto-select the most relevant tab only when a new search completes (status transitions to success).
  // Uses a ref to track the previous status so it only fires on the loading→success transition,
  // not on every render that happens to have rankStatus==="success".
  const prevRankStatusForTabRef = useRef<string>("idle");
  useEffect(() => {
    const prev = prevRankStatusForTabRef.current;
    prevRankStatusForTabRef.current = rankStatus;
    if (prev !== "loading" || rankStatus !== "success") return;
    if (matchedSpaces.length > 0) setActiveTab("recommended");
    else if (otherSpaces.length > 0) setActiveTab("other");
    else if (displayCombinations.length > 0) setActiveTab("combinations");
  }, [
    rankStatus,
    matchedSpaces.length,
    otherSpaces.length,
    displayCombinations.length,
  ]);

  useEffect(() => {
    const prev = prevRankStatusRef.current;
    prevRankStatusRef.current = rankStatus;
    if (prev !== "loading") return;

    const durationMs = searchStartTimeRef.current
      ? Date.now() - searchStartTimeRef.current
      : null;
    const timestamp = new Date().toISOString();
    const commonData = {
      deal_id: diagnosis?.dealId || null,
      user_id: userInfo?.user?.id ?? null,
      email: userInfo?.profile?.email ?? null,
      timestamp,
      duration_ms: durationMs,
      search_source: effectiveSearchSource ?? undefined,
      tolerance_capacity: tolCapIgnored ? 1.0 : tolCapValue,
      tolerance_price: tolPriceIgnored ? 1.0 : tolPriceValue,
      tolerance_distance: toleranceForSearch().distance,
    };

    if (rankStatus === "success") {
      const matched = rankedSpaces.filter((s) => s.score >= 80);
      sendTrackDataServer({
        eventName: "VISION_AI_MODE_LOCATION_SEARCH_SUCCESS",
        productType: "PO",
        url: window.location.pathname,
        data: { ...commonData, results_count: rankedSpaces.length },
      });
      sendTrackDataServer({
        eventName: "VISION_AI_MODE_SEARCH_RESULTS_RENDERED",
        productType: "PO",
        url: window.location.pathname,
        data: {
          ...commonData,
          has_results: matched.length > 0,
          results_count: rankedSpaces.length,
          matched_count: matched.length,
          matched_space_ids: matched.map((s) => s.id),
          matched_spaces: matched.map((s) => ({
            id: s.id,
            space_name: s.spaceName,
            room_name: s.roomName,
            score: s.score,
            capacity: s.capacity,
            monthly_price: s.monthlyPrice,
          })),
          filter_location_label: location?.label ?? null,
          filter_location_lat: location?.lat ?? null,
          filter_location_lng: location?.lng ?? null,
          filter_radius_km: radiusKm,
          filter_capacity_min: capacityMin ? Number(capacityMin) : null,
          filter_capacity_max: capacityMax ? Number(capacityMax) : null,
          filter_credits_value: FIXED_CREDIT_PRICE,
          filter_price_min: priceMin ? Number(priceMin) : null,
          filter_price_max: priceMax ? Number(priceMax) : null,
          filter_location_mode: locationMode,
          filter_city_name:
            locationMode === "neighborhoods" ? (neighCity?.name ?? null) : null,
          filter_neighborhood_names:
            locationMode === "neighborhoods"
              ? neighSelectedNeighborhoods.map((n) => n.name)
              : null,
        },
      });
      if (rankedSpaces.length === 0) {
        sendTrackDataServer({
          eventName: "VISION_AI_MODE_LOCATION_SEARCH_NO_RESULTS",
          productType: "PO",
          url: window.location.pathname,
          data: commonData,
        });
      }
      if (isQuickMode) {
        sendTrackDataServer({
          eventName: "QUICK_SEARCH_RESULT_VIEWED",
          productType: "PO",
          url: window.location.pathname,
          data: {
            user_id: userInfo?.user?.id ?? null,
            email: userInfo?.profile?.email ?? null,
            has_results: rankedSpaces.length > 0,
            results_count: rankedSpaces.length,
            spaces_returned_ids: rankedSpaces.map((s) => s.id),
          },
        });
        // Log de auditoria exigido pelo comercial (PED-12650). Fire-and-forget:
        // falha no log nunca pode bloquear a busca.
        if (userInfo?.user?.id) {
          const fullName = [userInfo.user.first_name, userInfo.user.last_name]
            .filter(Boolean)
            .join(" ");
          visionService
            .logQuickSearch({
              user_id: String(userInfo.user.id),
              user_name: fullName || undefined,
              user_email: userInfo.profile?.email ?? undefined,
              filters: (lastSearchParamsRef.current ?? {}) as unknown as Record<
                string,
                unknown
              >,
              results_count: rankedSpaces.length,
              space_ids: rankedSpaces.map((s) => s.id),
            })
            .catch((error) =>
              reportError(error, {
                operation: "logQuickSearch",
                step: "location",
              }),
            );
        }
      }
    } else if (rankStatus === "error") {
      sendTrackDataServer({
        eventName: "VISION_AI_MODE_LOCATION_SEARCH_ERROR",
        productType: "PO",
        url: window.location.pathname,
        data: commonData,
      });
    }
  }, [rankStatus, effectiveSearchSource]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = useCallback(() => {
    if (!diagnosis) return;

    if (locationMode === "neighborhoods") {
      if (!neighCity) return;
      const params = buildCurrentSearchModeParams();
      lastSearchParamsRef.current = params;
      searchStartTimeRef.current = Date.now();
      setSelectedSpaceIds((prev) =>
        prev.filter(
          (id) => originalSpaceIds.has(id) || idAddedSpaceIds.has(id),
        ),
      );
      setSelectedComboIds([]);
      searchMode(params);
      return;
    }

    const hasCoords =
      location &&
      location.lat != null &&
      location.lng != null &&
      !(location.lat === 0 && location.lng === 0);
    if (!hasCoords) return;
    sendTrackDataServer({
      eventName: "VISION_AI_MODE_LOCATION_SEARCH_STARTED",
      productType: "PO",
      url: window.location.pathname,
      data: {
        deal_id: diagnosis.dealId || null,
        user_id: userInfo?.user?.id ?? null,
        email: userInfo?.profile?.email ?? null,
        timestamp: new Date().toISOString(),
        location_label: location?.label ?? null,
        radius_km: radiusKm,
        capacity_min: capacityMin ? Number(capacityMin) : null,
        capacity_max: capacityMax ? Number(capacityMax) : null,
        credits_value: FIXED_CREDIT_PRICE,
        price_min: priceMin ? Number(priceMin) : null,
        price_max: priceMax ? Number(priceMax) : null,
        search_source: effectiveSearchSource ?? undefined,
        ...(() => {
          const t = toleranceForSearch();
          return {
            tolerance_capacity: t.capacity,
            tolerance_price: t.price,
            tolerance_distance: t.distance,
          };
        })(),
      },
    });
    const params = buildCurrentSearchModeParams();
    lastSearchParamsRef.current = params;
    searchStartTimeRef.current = Date.now();
    setSelectedSpaceIds((prev) =>
      prev.filter((id) => originalSpaceIds.has(id) || idAddedSpaceIds.has(id)),
    );
    setSelectedComboIds([]);
    searchMode(params);
  }, [
    diagnosis,
    location,
    radiusKm,
    capacityMin,
    capacityMax,
    priceMin,
    priceMax,
    searchMode,
    buildCurrentSearchModeParams,
    originalSpaceIds,
    idAddedSpaceIds,
    userInfo,
    effectiveSearchSource,
    locationMode,
    neighCity,
    toleranceForSearch,
  ]);

  const handleRetry = useCallback(() => {
    if (!lastSearchParamsRef.current) return;
    sendTrackDataServer({
      eventName: "VISION_AI_MODE_LOCATION_SEARCH_STARTED",
      productType: "PO",
      url: window.location.pathname,
      data: {
        deal_id: diagnosis?.dealId || null,
        user_id: userInfo?.user?.id ?? null,
        email: userInfo?.profile?.email ?? null,
        timestamp: new Date().toISOString(),
        is_retry: true,
        search_source: effectiveSearchSource ?? undefined,
        tolerance_capacity: tolCapIgnored ? 1.0 : tolCapValue,
        tolerance_price: tolPriceIgnored ? 1.0 : tolPriceValue,
        tolerance_distance: toleranceForSearch().distance,
      },
    });
    searchStartTimeRef.current = Date.now();
    setSelectedSpaceIds((prev) =>
      prev.filter((id) => originalSpaceIds.has(id) || idAddedSpaceIds.has(id)),
    );
    setSelectedComboIds([]);
    searchMode(lastSearchParamsRef.current);
  }, [
    searchMode,
    diagnosis,
    userInfo,
    originalSpaceIds,
    idAddedSpaceIds,
    toleranceForSearch,
    effectiveSearchSource,
    tolCapIgnored,
    tolCapValue,
    tolPriceIgnored,
    tolPriceValue,
  ]);

  const hasValidLocationSelection =
    !!location &&
    location.lat != null &&
    location.lng != null &&
    !(location.lat === 0 && location.lng === 0);
  const hasValidNeighborhoodsSelection =
    locationMode === "neighborhoods" && !!neighCity;
  const isValid =
    (hasValidLocationSelection || hasValidNeighborhoodsSelection) &&
    proposalRoomIds.size > 0;

  const handleNextRef = useRef<() => void>(() => {});
  handleNextRef.current = () => {
    if (!isValid) return;
    onNext({
      location,
      radiusKm,
      capacityMin: capacityMin !== "" ? Number(capacityMin) : null,
      capacityMax: capacityMax !== "" ? Number(capacityMax) : null,
      creditsPrice: FIXED_CREDIT_PRICE,
      selectedSpaceIds,
      selectedComboIds,
      rankedSpaces: allRankedSpacesForSave,
      extraSpaces:
        displayExtraSpaces.length > 0
          ? displayExtraSpaces
          : (data?.extraSpaces ?? []),
      idAddedSpaces,
      roomCombinations:
        displayCombinations.length > 0 ? displayCombinations : undefined,
      recommendationHash: displayRecommendationHash,
      searchSource: effectiveSearchSource,
      locationMode,
      cityName: neighCity?.name,
      neighborhoodNames: neighSelectedNeighborhoods.map((n) => n.name),
      includeUnavailableWithinPeriod: includeUnavailableWithinPeriodRef.current,
      toleranceCapacity: tolCapIgnored ? 1.0 : tolCapValue,
      toleranceCapacityIgnored: tolCapIgnored,
      tolerancePrice: tolPriceIgnored ? 1.0 : tolPriceValue,
      tolerancePriceIgnored: tolPriceIgnored,
      toleranceDistance: toleranceForSearch().distance,
      toleranceDistanceIgnored: tolDistIgnored,
      toleranceDistanceKm: tolDistKm ? parseFloat(tolDistKm) : null,
      priceMin: priceMin !== "" ? Number(priceMin) : null,
      priceMax: priceMax !== "" ? Number(priceMax) : null,
    });
  };

  useEffect(() => {
    registerNext?.(() => handleNextRef.current());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  useEffect(() => {
    if (!registerOnMarkerClick) return;
    registerOnMarkerClick((id: string) => {
      // Find which tab the space belongs to and switch to it
      if (matchedSpaces.some((s) => s.id === id)) {
        setActiveTab("recommended");
      } else if (otherSpaces.some((s) => s.id === id)) {
        setActiveTab("other");
      } else if (
        displayCombinations.some((c) =>
          c.rooms.some((r) => r.privateRoomId === id),
        )
      ) {
        setActiveTab("combinations");
      }
      activeCombinationIdRef.current = null;
      setActiveSpaceId(id);
      onPreviewUpdate?.({ activeSpaceId: id, activeCombination: null });
      // Scroll the card into view after React re-renders the tab content
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = scrollContainerRef.current?.querySelector(
            `[data-space-id="${id}"]`,
          );
          el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        });
      });
    });
  }, [
    registerOnMarkerClick,
    matchedSpaces,
    otherSpaces,
    displayCombinations,
    onPreviewUpdate,
  ]);

  useEffect(() => {
    onPreviewUpdate?.({
      location,
      radiusKm,
      activeSpaceId,
      showOtherSpaces,
      showOutdatedSpaces,
      selectedSpaceIds,
    });
  }, [
    location,
    radiusKm,
    activeSpaceId,
    showOtherSpaces,
    showOutdatedSpaces,
    selectedSpaceIds,
    onPreviewUpdate,
  ]);

  useEffect(() => {
    onPreviewUpdate?.({ outdatedSpaces: filteredOutdatedSpaces });
  }, [filteredOutdatedSpaces, onPreviewUpdate]);

  useEffect(() => {
    onDiagnosisSync?.({
      positionsMin: capacityMin !== "" ? Number(capacityMin) : "",
      positionsMax: capacityMax !== "" ? Number(capacityMax) : "",
      targetCity: location?.label ?? "",
      selectedLocation: location ?? null,
      includeUnavailableWithinPeriod: includeUnavailableWithinPeriodRef.current,
    });
  }, [
    location,
    capacityMin,
    capacityMax,
    unavailableDaysEnabled,
    unavailableDaysValue,
    onDiagnosisSync,
  ]);

  // Remove ID-added spaces that have been deselected
  useEffect(() => {
    setIdAddedSpaces((prev) =>
      prev.filter((s) => selectedSpaceIds.includes(s.id)),
    );
  }, [selectedSpaceIds]);

  /** Busca salas por IDs usando o novo endpoint /search com mode=room */
  const fetchRoomsByIds = useCallback(
    async (
      ids: string[],
      unitCreditPrice = FIXED_CREDIT_PRICE,
    ): Promise<RankedSpace[]> => {
      const response = await privateOfficeService.searchByMode({
        mode: "room" as const,
        roomIds: ids,
        filters: {
          status: [0, 1, 2, 3, 4, 5, 6],
          approvalStatus: "approved",
          includeLegacy: true,
          includeDeleted: true,
          unitCreditPrice,
          skipRecentUpdatesFilter: true,
        },
      });
      const results = response.results ?? [];
      const extra = response.extra_rooms ?? [];
      return mapSearchResultToRankedSpaces([...results, ...extra]);
    },
    [],
  );

  // Fetch fresh data for original spaces (edit mode) and compute diffs
  useEffect(() => {
    if (!originalSpaces?.length) return;
    const ids = originalSpaces.map((s) => s.id).filter(Boolean);
    if (!ids.length) return;

    fetchRoomsByIds(ids)
      .then((fresh) => {
        const freshById = new Map(fresh.map((s) => [s.id, s]));
        const diffs = new Map<string, SpaceDiff>();
        const refreshed = new Map<string, RankedSpace>();

        for (const orig of originalSpaces) {
          const freshSpace = freshById.get(orig.id);
          if (!freshSpace) continue;
          const fields = computeSpaceDiff(orig, freshSpace);
          if (fields.length > 0) {
            diffs.set(orig.id, { spaceId: orig.id, fields, freshSpace });
          }
          // Document flags follow the inventory without asking: they are
          // not the executive's to accept or reject (PED-14095).
          const withFlags = withFreshComplianceFlags(orig, freshSpace);
          if (withFlags) refreshed.set(orig.id, withFlags);
        }

        setSpaceDiffs(diffs);
        if (refreshed.size > 0) {
          setUpdatedOriginalSpaces((prev) => {
            const next = new Map(prev);
            refreshed.forEach((space, id) => {
              // A room whose diff was already accepted keeps those accepted
              // fields; only the document flags follow the inventory.
              next.set(id, {
                ...(next.get(id) ?? space),
                spaceHasAvcb: space.spaceHasAvcb,
                spaceHasOperatingPermit: space.spaceHasOperatingPermit,
              });
            });
            return next;
          });
        }
      })
      .catch(() => {
        // diff is non-critical — silently ignore
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot on mount; re-running would discard already-accepted diffs
  }, [originalSpaces]);

  const handleAcceptDiff = useCallback((fresh: RankedSpace) => {
    setUpdatedOriginalSpaces((prev) => new Map(prev).set(fresh.id, fresh));
    setSpaceDiffs((prev) => {
      const next = new Map(prev);
      next.delete(fresh.id);
      return next;
    });
    setDiffModalTarget(null);
  }, []);

  const handleAddById = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = addByIdValue.trim();
      if (!trimmed) return;

      if (
        isDuplicateAddById(
          trimmed,
          selectedSpaceIds,
          originalSpaceIds,
          idAddedSpaceIds,
          addedByIdInputs,
        )
      ) {
        setAddByIdStatus("error");
        setAddByIdError("Esta sala já está na lista.");
        return;
      }

      setAddByIdStatus("loading");
      setAddByIdError(null);

      try {
        const rooms = await fetchRoomsByIds([trimmed]);

        if (!rooms.length) {
          setAddByIdError(`Sala não encontrada para o ID "${trimmed}".`);
          setAddByIdStatus("error");
          return;
        }

        const [mapped] = rooms;
        const space: RankedSpace = {
          ...mapped,
          addedById: true,
          addedByIdValue: trimmed,
        };

        if (
          isDuplicateAddById(
            space.id,
            selectedSpaceIds,
            originalSpaceIds,
            idAddedSpaceIds,
            addedByIdInputs,
          )
        ) {
          setAddByIdError("Esta sala já está na lista.");
          setAddByIdStatus("error");
          return;
        }

        setIdAddedSpaces((prev) => [space, ...prev]);
        setSelectedSpaceIds((prev) =>
          prev.includes(space.id) ? prev : [...prev, space.id],
        );
        setAddByIdFoundSpace(space);
        setAddByIdStatus("success");

        sendTrackDataServer({
          eventName: "VISION_AI_MODE_LOCATION_SPACE_ADDED_BY_ID",
          productType: "PO",
          url: window.location.pathname,
          data: {
            deal_id: diagnosis?.dealId || null,
            user_id: userInfo?.user?.id ?? null,
            room_id: space.id,
            found: true,
          },
        });
      } catch {
        setAddByIdError("Ocorreu um erro. Tente novamente.");
        setAddByIdStatus("error");
      }
    },
    [
      addByIdValue,
      fetchRoomsByIds,
      originalSpaceIds,
      idAddedSpaceIds,
      addedByIdInputs,
      diagnosis?.dealId,
      userInfo,
    ],
  );

  const handleLocationSelect = useCallback(
    async (spaceData?: { address: string; name: string; placeId: string }) => {
      if (!spaceData) {
        setLocation(null);
        setLocationInputValue("");
        return;
      }
      const label =
        `${spaceData?.name ?? ""}${spaceData?.address ? ` - ${spaceData.address}` : ""}`.trim();

      let lat = -15.7942;
      let lng = -47.8822;

      try {
        const response = await spaceService.getSpaceByLocation(label);
        const result = Array.isArray(response) ? response[0] : response;
        if (result?.lat != null && result?.lng != null) {
          lat = Number(result.lat);
          lng = Number(result.lng);
        }
      } catch {
        // keep defaults
      }

      setLocation({ label, lat, lng });
      setLocationInputValue(label);
    },
    [],
  );

  const toggleSpace = useCallback((id: string) => {
    setSelectedSpaceIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  }, []);

  const buildCombinationScoreCriteria = (
    combo: RoomCombination,
  ): ScoreAnalysis[] => {
    const tol = toleranceForSearch();
    const criteria: ScoreAnalysis[] = [];

    if (radiusKm && combo.distanceKm >= 0) {
      const within = combo.distanceKm <= radiusKm * (1 + tol.distance);
      criteria.push({
        criterion: "distance",
        requested: `${radiusKm}km radius`,
        offered: `${combo.distanceKm.toFixed(1)}km`,
        matched: combo.distanceKm <= radiusKm,
        inTolerance: !within ? false : combo.distanceKm > radiusKm,
      });
    }
    if (capacityMin !== "") {
      const min = Number(capacityMin);
      criteria.push({
        criterion: "min_capacity",
        requested: `min ${min}`,
        offered: `${combo.totalCapacity}`,
        matched: combo.totalCapacity >= min,
        inTolerance:
          combo.totalCapacity < min &&
          combo.totalCapacity >= min * (1 - tol.capacity),
      });
    }
    if (capacityMax !== "") {
      const max = Number(capacityMax);
      criteria.push({
        criterion: "max_capacity",
        requested: `max ${max}`,
        offered: `${combo.totalCapacity}`,
        matched: combo.totalCapacity <= max,
        inTolerance:
          combo.totalCapacity > max &&
          combo.totalCapacity <= max * (1 + tol.capacity),
      });
    }
    if (priceMin !== "") {
      const min = Number(priceMin);
      criteria.push({
        criterion: "min_price",
        requested: `min ${min}`,
        offered: `${combo.totalMonthlyPrice}`,
        matched: combo.totalMonthlyPrice >= min,
        inTolerance:
          combo.totalMonthlyPrice < min &&
          combo.totalMonthlyPrice >= min * (1 - tol.price),
      });
    }
    if (priceMax !== "") {
      const max = Number(priceMax);
      criteria.push({
        criterion: "max_price",
        requested: `max ${max}`,
        offered: `${combo.totalMonthlyPrice}`,
        matched: combo.totalMonthlyPrice <= max,
        inTolerance:
          combo.totalMonthlyPrice > max &&
          combo.totalMonthlyPrice <= max * (1 + tol.price),
      });
    }
    return criteria;
  };

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const activeCombinationIdRef = useRef<string | null>(null);

  const activateCombination = useCallback(
    (combo: RoomCombination) => {
      const isAlreadyActive =
        activeCombinationIdRef.current === combo.combinationId;
      if (isAlreadyActive) {
        activeCombinationIdRef.current = null;
        setActiveSpaceId(null);
        onPreviewUpdate?.({ activeSpaceId: null, activeCombination: null });
        return;
      }

      activeCombinationIdRef.current = combo.combinationId;
      const allAvailableSpaces = [
        ...filteredDisplaySpaces,
        ...displayExtraSpaces,
        ...idAddedSpaces,
      ];
      const fullRooms = combo.rooms.map((r) =>
        allAvailableSpaces.find((s) => s.id === r.privateRoomId),
      );
      const firstRoom = fullRooms.find(Boolean);

      const activeCombination: ActiveCombinationPreview = {
        combinationId: combo.combinationId,
        lat: firstRoom?.lat ?? 0,
        lng: firstRoom?.lng ?? 0,
        rooms: combo.rooms.map((r, i) => ({
          id: r.privateRoomId,
          name: r.label,
          images: fullRooms[i]?.images ?? [],
        })),
      };

      setActiveSpaceId(firstRoom?.id ?? null);
      onPreviewUpdate?.({
        activeSpaceId: firstRoom?.id ?? null,
        activeCombination,
      });
    },
    [filteredDisplaySpaces, displayExtraSpaces, idAddedSpaces, onPreviewUpdate],
  );

  // Combo selection is its own dimension: toggle the combinationId only. It does
  // NOT touch selectedSpaceIds, so the combo's rooms don't appear checked in the
  // recommended/other (singles) tabs unless the user also picks them individually.
  const toggleCombination = useCallback((combo: RoomCombination) => {
    setSelectedComboIds((prev) =>
      prev.includes(combo.combinationId)
        ? prev.filter((id) => id !== combo.combinationId)
        : [...prev, combo.combinationId],
    );
  }, []);

  const capacityLabel = (() => {
    if (!capacityMin && !capacityMax) return null;
    if (capacityMin && capacityMax) return `${capacityMin}–${capacityMax}`;
    if (capacityMin) return `${capacityMin}+`;
    return `até ${capacityMax}`;
  })();

  const creditsLabel = `R$ ${FIXED_CREDIT_PRICE.toLocaleString("pt-BR")}`;

  const priceLabel = (() => {
    if (!priceMin && !priceMax) return null;
    const fmt = (v: string) => {
      const n = Number(v);
      return n >= 1000 ? `R$${(n / 1000).toFixed(0)}k` : `R$${n}`;
    };
    if (priceMin && priceMax) return `${fmt(priceMin)}–${fmt(priceMax)}`;
    if (priceMin) return `${fmt(priceMin)}+`;
    return `até ${fmt(priceMax)}`;
  })();

  const renderCard = (
    space: RankedSpace,
    opts?: { onDiffClick?: () => void },
  ) => (
    <div key={space.id} data-space-id={space.id}>
      <SpaceCard
        space={space}
        onDiffClick={opts?.onDiffClick}
        selected={selectedSpaceIds.includes(space.id)}
        active={activeSpaceId === space.id}
        onToggleSelect={() => toggleSpace(space.id)}
        onActivate={() => {
          const next = activeSpaceId === space.id ? null : space.id;
          activeCombinationIdRef.current = null;
          setActiveSpaceId(next);
          onPreviewUpdate?.({ activeSpaceId: next, activeCombination: null });
        }}
        onViewDetails={() => {
          sendTrackDataServer({
            eventName: "VISION_AI_MODE_SPACE_DETAILS_VIEWED",
            productType: "PO",
            url: window.location.pathname,
            data: {
              deal_id: diagnosis?.dealId || null,
              user_id: userInfo?.user?.id ?? null,
              email: userInfo?.profile?.email ?? null,
              room_id: space.id,
              room_name: space.roomName,
              room_search_id: space.searchId ?? null,
              space_id: space.partnerId?.toString() ?? "",
              space_name: space.spaceName,
              space_score: space.score,
              space_city: space.city,
              space_monthly_price: space.monthlyPrice ?? null,
              space_capacity: space.capacity,
            },
          });
          setDrawerSpace(space);
        }}
        onScoreClick={() => {
          sendTrackDataServer({
            eventName: "VISION_AI_MODE_SPACE_SCORE_VIEWED",
            productType: "PO",
            url: window.location.pathname,
            data: {
              deal_id: diagnosis?.dealId || null,
              user_id: userInfo?.user?.id ?? null,
              email: userInfo?.profile?.email ?? null,
              space_id: space.id,
              space_name: space.spaceName,
              space_score: space.score,
            },
          });
        }}
      />
    </div>
  );

  return (
    <div className="flex flex-col h-full gap-2">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Localização</h2>
        {displayRankStatus === "idle" && (
          <p className="text-sm text-gray-500 mt-0.5">
            Defina a região e selecione os espaços
          </p>
        )}
      </div>

      {/* Aviso fixo do modo busca rápida (não modal) — PED-12650 */}
      {isQuickMode && (
        <div
          role="note"
          className="flex items-start gap-2 text-xs rounded-xl px-3 py-2.5"
          style={{
            background: "#FFFBEB",
            border: "1px solid rgba(217,119,6,0.2)",
            color: "#B45309",
          }}
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            Busca rápida ativa — os dados desta busca não serão capturados nem
            associados a um deal.
          </span>
        </div>
      )}

      {/* Search bar */}
      <div className="flex flex-col gap-2">
        {/* Row 1: location input + radius + actions */}
        <div className="flex items-center gap-1.5">
          {/* Location display — input-like, opens sheet */}
          <button
            type="button"
            onClick={() => setFiltersSheetOpen(true)}
            className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-left min-w-0 transition-colors hover:bg-[#F7F5F2]"
            style={{ border: "1px solid #E8E6E3", background: "white" }}
          >
            {locationMode === "location" ? (
              <MapPin
                className="w-3.5 h-3.5 shrink-0"
                style={{ color: location ? "#1A1814" : "#C8C4BE" }}
              />
            ) : (
              <LayoutGrid
                className="w-3.5 h-3.5 shrink-0"
                style={{ color: neighCity ? "#1A1814" : "#C8C4BE" }}
              />
            )}
            <span
              className="truncate"
              style={{
                color:
                  (locationMode === "location" ? location : neighCity) != null
                    ? "#1A1814"
                    : "#9E9A94",
              }}
            >
              {locationMode === "location"
                ? (location?.label ?? "Buscar localização...")
                : neighCity != null
                  ? `${neighCity.name}${neighSelectedNeighborhoods.length > 0 ? ` · ${neighSelectedNeighborhoods.length} bairros` : ""}`
                  : "Selecionar cidade e bairros..."}
            </span>
          </button>

          {/* Adicionar por ID */}
          <button
            type="button"
            onClick={() => {
              setAddByIdOpen(true);
              setAddByIdError(null);
              setAddByIdStatus("idle");
              setAddByIdFoundSpace(null);
              setAddByIdValue("");
            }}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium shrink-0 transition-colors hover:bg-[#ECEAE6]"
            style={{ background: "#F0EEEB", color: "#43403D" }}
          >
            <Hash className="w-3.5 h-3.5" />
            ID
          </button>

          {/* Filtros */}
          <button
            type="button"
            onClick={() => setFiltersSheetOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium shrink-0 transition-colors hover:bg-[#ECEAE6]"
            style={{ background: "#F0EEEB", color: "#43403D" }}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            Filtros
          </button>

          {/* Buscar */}
          <button
            type="button"
            onClick={handleSearch}
            disabled={
              rankStatus === "loading" ||
              (locationMode === "location"
                ? !hasValidLocationSelection
                : !neighCity)
            }
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold shrink-0 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            style={{
              background: "#1A1814",
              color: "#FFD900",
              letterSpacing: "0.02em",
              ...(rankStatus === "loading" && {
                backgroundImage:
                  "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.07) 50%, transparent 60%)",
                backgroundSize: "200% 100%",
                animation: "shimmer 5s ease-in-out infinite",
              }),
            }}
          >
            <Search className="w-3.5 h-3.5" />
            {rankStatus === "loading" ? "Buscando..." : "Buscar"}
          </button>
        </div>

        {/* Row 2: active filter chips + add by ID — horizontal scroll */}
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none">
          {locationMode === "location" && radiusKm != null && (
            <button
              type="button"
              onClick={() => setFiltersSheetOpen(true)}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium shrink-0 transition-all"
              style={{ background: "#F0EEEB", color: "#43403D" }}
            >
              <CircleDashed className="w-3 h-3" />
              {radiusKm} km
            </button>
          )}
          {capacityLabel && (
            <button
              type="button"
              onClick={() => {
                setCapacityMin("");
                setCapacityMax("");
              }}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium shrink-0 transition-all"
              style={{ background: "#F0EEEB", color: "#43403D" }}
            >
              <Users className="w-3 h-3" />
              {capacityLabel}
              <X className="w-3 h-3 ml-0.5 opacity-50" />
            </button>
          )}
          <span
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium shrink-0"
            style={{ background: "#F0EEEB", color: "#43403D" }}
          >
            <Coins className="w-3 h-3" />
            {creditsLabel}
          </span>
          {priceLabel && (
            <button
              type="button"
              onClick={() => {
                setPriceMin("");
                setPriceMax("");
              }}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium shrink-0 transition-all"
              style={{ background: "#F0EEEB", color: "#43403D" }}
            >
              <DollarSign className="w-3 h-3" />
              {priceLabel}
              <X className="w-3 h-3 ml-0.5 opacity-50" />
            </button>
          )}
          {unavailableDaysEnabled && unavailableDaysValue && (
            <button
              type="button"
              onClick={() => setUnavailableDaysEnabled(false)}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium shrink-0 transition-all"
              style={{ background: "#F0EEEB", color: "#43403D" }}
            >
              <Clock className="w-3 h-3" />
              {unavailableDaysValue} dias
              <X className="w-3 h-3 ml-0.5 opacity-50" />
            </button>
          )}
        </div>

        {/* Add by ID modal */}
        <Dialog
          open={addByIdOpen}
          onOpenChange={(open) => {
            if (!open) {
              setAddByIdOpen(false);
              setAddByIdValue("");
              setAddByIdError(null);
              setAddByIdStatus("idle");
              setAddByIdFoundSpace(null);
            }
          }}
        >
          <DialogContent className="max-w-sm">
            {addByIdStatus === "success" && addByIdFoundSpace ? (
              <div className="flex flex-col items-center text-center gap-4 py-2">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ background: "#DCFCE7" }}
                >
                  <CheckCircle2
                    className="w-6 h-6"
                    style={{ color: "#16A34A" }}
                  />
                </div>
                <div className="space-y-1">
                  <DialogTitle
                    className="text-base font-semibold"
                    style={{ color: "#1A1814" }}
                  >
                    Sala adicionada
                  </DialogTitle>
                  <DialogDescription
                    className="text-sm"
                    style={{ color: "#78736D" }}
                  >
                    {addByIdFoundSpace.roomName}
                    {addByIdFoundSpace.spaceName
                      ? ` · ${addByIdFoundSpace.spaceName}`
                      : ""}
                  </DialogDescription>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setAddByIdOpen(false);
                    setAddByIdValue("");
                    setAddByIdError(null);
                    setAddByIdStatus("idle");
                    setAddByIdFoundSpace(null);
                  }}
                  className="w-full py-2 rounded-lg text-sm font-semibold transition-opacity hover:opacity-90"
                  style={{ background: "#FFD900", color: "#1A1814" }}
                >
                  Fechar
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-4 py-2">
                <div className="space-y-1">
                  <DialogTitle
                    className="text-base font-semibold"
                    style={{ color: "#1A1814" }}
                  >
                    Adicionar sala por ID
                  </DialogTitle>
                  <DialogDescription
                    className="text-sm"
                    style={{ color: "#78736D" }}
                  >
                    Informe o ID da sala para adicioná-la diretamente à lista.
                  </DialogDescription>
                </div>

                <form onSubmit={handleAddById} className="flex flex-col gap-3">
                  {(() => {
                    const trimmed = addByIdValue.trim();
                    const isDuplicate =
                      trimmed.length > 0 &&
                      (originalSpaceIds.has(trimmed) ||
                        idAddedSpaceIds.has(trimmed) ||
                        addedByIdInputs.has(trimmed));
                    return (
                      <>
                        <input
                          autoFocus
                          inputMode="numeric"
                          pattern="[0-9]*"
                          type="text"
                          value={addByIdValue}
                          onChange={(e) => {
                            const numeric = e.target.value.replace(/\D/g, "");
                            setAddByIdValue(numeric);
                            setAddByIdError(null);
                          }}
                          placeholder="Ex: 6709"
                          className="w-full text-sm px-3 py-2 rounded-lg border bg-white outline-none focus:ring-2 focus:ring-yellow-400/60"
                          style={{
                            borderColor: isDuplicate ? "#FCA5A5" : "#E8E6E3",
                            color: "#1A1814",
                          }}
                          disabled={addByIdStatus === "loading"}
                        />

                        {isDuplicate && (
                          <div
                            className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg"
                            style={{ background: "#FEF2F2", color: "#DC2626" }}
                          >
                            <AlertCircle className="w-4 h-4 shrink-0" />
                            <span>Sala já adicionada à lista.</span>
                          </div>
                        )}

                        {!isDuplicate &&
                          addByIdStatus === "error" &&
                          addByIdError && (
                            <div
                              className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg"
                              style={{
                                background: "#FEF2F2",
                                color: "#DC2626",
                              }}
                            >
                              <AlertCircle className="w-4 h-4 shrink-0" />
                              <span>{addByIdError}</span>
                            </div>
                          )}

                        <button
                          type="submit"
                          disabled={
                            !trimmed ||
                            isDuplicate ||
                            addByIdStatus === "loading"
                          }
                          className="inline-flex items-center justify-center gap-2 w-full py-2 rounded-lg text-sm font-semibold transition-opacity disabled:opacity-40"
                          style={{ background: "#1A1814", color: "#FFD900" }}
                        >
                          {addByIdStatus === "loading" ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Buscando...
                            </>
                          ) : (
                            "Adicionar"
                          )}
                        </button>
                      </>
                    );
                  })()}
                </form>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters side sheet */}
      <Sheet open={filtersSheetOpen} onOpenChange={setFiltersSheetOpen}>
        <SheetContent
          side="right"
          className="px-0 pb-0 w-[520px] sm:max-w-[520px] flex flex-col"
        >
          <SheetHeader className="px-5 pb-3 pt-1">
            <SheetTitle className="text-base">Configurar busca</SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-5 pb-6 space-y-5">
            {/* Mode toggle */}
            <div
              className="flex rounded-full p-0.5"
              style={{ background: "#F0EEEB", border: "1px solid #E8E6E3" }}
            >
              <button
                type="button"
                onClick={() => setLocationMode("location")}
                className="flex items-center gap-1.5 flex-1 justify-center py-1.5 rounded-full text-xs font-medium transition-all"
                style={
                  locationMode === "location"
                    ? { background: "#1A1814", color: "white" }
                    : { background: "transparent", color: "#78736D" }
                }
              >
                <MapPin className="w-3 h-3" />
                Localização
              </button>
              <button
                type="button"
                onClick={() => setLocationMode("neighborhoods")}
                className="flex items-center gap-1.5 flex-1 justify-center py-1.5 rounded-full text-xs font-medium transition-all"
                style={
                  locationMode === "neighborhoods"
                    ? { background: "#1A1814", color: "white" }
                    : { background: "transparent", color: "#78736D" }
                }
              >
                <LayoutGrid className="w-3 h-3" />
                Múltiplos bairros
              </button>
            </div>

            {/* Location inputs */}
            {locationMode === "location" ? (
              <div>
                <SearchAutocomplete
                  ref={locationRef}
                  placeholder="Buscar cidade, bairro ou endereço..."
                  className="!mt-0"
                  listClassName="!absolute !left-0 !w-full !top-auto"
                  options={spaces.map((s) => ({
                    value: s.placeId,
                    label: s.name || s.address,
                    description: s.address,
                    data: s,
                  }))}
                  value={locationInputValue}
                  onValueChange={(_, spaceData) =>
                    handleLocationSelect(spaceData)
                  }
                  onInputChange={(value) => {
                    setLocationInputValue(value);
                    setLocation(null);
                    handleSearchSpaces(value);
                  }}
                  isLoading={isLoading}
                />
                {locationInputValue.trim() && !hasValidLocationSelection && (
                  <p
                    className="text-xs mt-1"
                    style={{ color: "#B91C1C" }}
                    role="alert"
                  >
                    Selecione uma localização da lista para continuar.
                  </p>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <SearchInputCommand
                  name="cities"
                  selectOption={(_, item) => setNeighCity(item)}
                  searchValue={neighCity?.name ?? ""}
                  defaultValue={neighCity?.name ?? ""}
                  optionsList={filterCities ?? []}
                  updateList={filterHandSelectedCity}
                  label="Cidade"
                  inputWidth="w-full"
                />
                <SearchInputCommand
                  name="neighborhood"
                  chevron
                  selectOption={(_, item) =>
                    setNeighSelectedNeighborhoods((prev) =>
                      prev.find((n) => n.id === item.id)
                        ? prev.filter((n) => n.id !== item.id)
                        : [...prev, item],
                    )
                  }
                  selectedList={neighSelectedNeighborhoods}
                  searchValue=""
                  optionsList={filterNeighborhoods ?? []}
                  updateList={(term: string) =>
                    filterHandSelectedNeighborhood(term, neighCity?.name ?? "")
                  }
                  label="Bairros"
                  inputWidth="w-full"
                  disabled={!neighCity}
                />
                {neighSelectedNeighborhoods.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {neighSelectedNeighborhoods.map((n) => (
                      <span
                        key={n.id}
                        className="flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium"
                        style={{ background: "#F0EEEB", color: "#43403D" }}
                      >
                        {n.name}
                        <button
                          type="button"
                          onClick={() =>
                            setNeighSelectedNeighborhoods((prev) =>
                              prev.filter((x) => x.id !== n.id),
                            )
                          }
                          className="flex items-center justify-center rounded-full w-3.5 h-3.5 hover:bg-black/10 transition-colors"
                          style={{ color: "#78736D" }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div style={{ height: 1, background: "#E8E6E3" }} />

            {/* Radius — location mode only */}
            {locationMode === "location" && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <MapPin className="w-3 h-3" style={{ color: "#9E9A94" }} />
                  <span
                    className="text-[10px] font-semibold uppercase"
                    style={{ color: "#9E9A94", letterSpacing: "0.08em" }}
                  >
                    Raio de busca
                  </span>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {RADIUS_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setRadiusKm(opt.value);
                        setCustomRadiusMode(false);
                        setCustomRadiusInput("");
                      }}
                      className="text-xs py-1 px-3 rounded-full font-medium transition-all duration-200"
                      style={
                        !customRadiusMode && radiusKm === opt.value
                          ? { background: "#1A1814", color: "white" }
                          : { background: "#F0EEEB", color: "#78736D" }
                      }
                    >
                      {opt.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setCustomRadiusMode(true)}
                    className="text-xs py-1 px-3 rounded-full font-medium transition-all duration-200"
                    style={
                      customRadiusMode
                        ? { background: "#1A1814", color: "white" }
                        : { background: "#F0EEEB", color: "#78736D" }
                    }
                  >
                    Outro
                  </button>
                  {customRadiusMode && (
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={4}
                      value={customRadiusInput}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/\D/g, "");
                        setCustomRadiusInput(raw);
                        const n = parseInt(raw, 10);
                        if (!isNaN(n) && n > 0) setRadiusKm(n);
                      }}
                      placeholder="km"
                      autoFocus
                      style={{
                        appearance: "none",
                        WebkitAppearance: "none",
                        fontFamily: "inherit",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        lineHeight: 1,
                        width: 52,
                        height: 28,
                        padding: "0 10px",
                        background: "#F0EEEB",
                        border: "none",
                        borderRadius: 9999,
                        color: "#1A1814",
                        textAlign: "center",
                        outline: "none",
                      }}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Capacity */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Users className="w-3 h-3" style={{ color: "#9E9A94" }} />
                <span
                  className="text-[10px] font-semibold uppercase"
                  style={{ color: "#9E9A94", letterSpacing: "0.08em" }}
                >
                  Capacidade
                </span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder="Mínimo"
                  value={capacityMin}
                  onChange={(e) => setCapacityMin(e.target.value)}
                  className="w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:border-[#FFD900]"
                  style={{ borderColor: "#E8E6E3" }}
                />
                <input
                  type="number"
                  placeholder="Máximo"
                  value={capacityMax}
                  onChange={(e) => setCapacityMax(e.target.value)}
                  className="w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:border-[#FFD900]"
                  style={{ borderColor: "#E8E6E3" }}
                />
              </div>
            </div>

            {/* Credits price — fixed by commercial policy */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Coins className="w-3 h-3" style={{ color: "#9E9A94" }} />
                <span
                  className="text-[10px] font-semibold uppercase"
                  style={{ color: "#9E9A94", letterSpacing: "0.08em" }}
                >
                  Preço do crédito
                </span>
              </div>
              <div
                className="w-full rounded-xl border px-3 py-2.5 text-sm"
                style={{ borderColor: "#E8E6E3", color: "#43403D" }}
              >
                R$ {FIXED_CREDIT_PRICE},00
              </div>
              <p className="text-[10px] mt-1" style={{ color: "#9E9A94" }}>
                Valor fixo definido pela política comercial
              </p>
            </div>

            {/* Monthly price */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <DollarSign className="w-3 h-3" style={{ color: "#9E9A94" }} />
                <span
                  className="text-[10px] font-semibold uppercase"
                  style={{ color: "#9E9A94", letterSpacing: "0.08em" }}
                >
                  Preço mensal
                </span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder="Mínimo"
                  value={priceMin}
                  onChange={(e) => setPriceMin(e.target.value)}
                  className="w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:border-[#FFD900]"
                  style={{ borderColor: "#E8E6E3" }}
                />
                <input
                  type="number"
                  placeholder="Máximo"
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                  className="w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:border-[#FFD900]"
                  style={{ borderColor: "#E8E6E3" }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3 h-3" style={{ color: "#9E9A94" }} />
                  <span
                    className="text-[10px] font-semibold uppercase"
                    style={{ color: "#9E9A94", letterSpacing: "0.08em" }}
                  >
                    Disponibilidade futura
                  </span>
                </div>
                <Switch
                  checked={unavailableDaysEnabled}
                  onCheckedChange={setUnavailableDaysEnabled}
                  aria-label="Incluir salas com disponibilidade futura"
                />
              </div>
              {unavailableDaysEnabled && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs" style={{ color: "#78736D" }}>
                    Disponíveis nos próximos
                  </span>
                  <input
                    type="number"
                    min={1}
                    value={unavailableDaysValue}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, "");
                      const n = parseInt(raw, 10);
                      setUnavailableDaysValue(
                        raw === "" ? "" : String(Math.max(1, n)),
                      );
                    }}
                    className="w-16 rounded-xl border px-2 py-1.5 text-sm text-center outline-none focus:border-[#FFD900]"
                    style={{ borderColor: "#E8E6E3" }}
                  />
                  <span className="text-xs" style={{ color: "#78736D" }}>
                    dias
                  </span>
                </div>
              )}
            </div>
            {/* Tolerance */}
            <>
              <div style={{ height: 1, background: "#E8E6E3" }} />
              <div
                className="rounded-xl overflow-hidden"
                style={{ border: "1px solid #E8E6E3" }}
              >
                {/* Header */}
                <div
                  className="flex items-center gap-1.5 px-3 py-2"
                  style={{
                    background: "#F7F5F2",
                    borderBottom: "1px solid #EDE9E4",
                  }}
                >
                  <SlidersHorizontal
                    className="w-3 h-3"
                    style={{ color: "#9E9A94" }}
                  />
                  <span
                    className="text-[10px] font-semibold uppercase"
                    style={{ color: "#9E9A94", letterSpacing: "0.08em" }}
                  >
                    Tolerância
                  </span>
                </div>

                {/* ── Capacidade ── */}
                <div
                  className="px-3 py-2.5"
                  style={{
                    background: "white",
                    borderBottom: "1px solid #F0EEEB",
                    opacity: tolCapIgnored ? 0.45 : 1,
                    transition: "opacity 0.15s",
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className="text-xs font-semibold"
                      style={{ color: "#43403D" }}
                    >
                      Capacidade
                    </span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <label className="flex items-center gap-1.5 cursor-pointer select-none">
                            <span
                              className="text-[11px]"
                              style={{ color: "#B8B4AE" }}
                            >
                              Ignorar
                            </span>
                            <Switch
                              checked={tolCapIgnored}
                              onCheckedChange={setTolCapIgnored}
                            />
                          </label>
                        </TooltipTrigger>
                        <TooltipContent
                          side="left"
                          className="max-w-[200px] text-center"
                        >
                          <p>
                            Quando ativado, a capacidade não elimina salas dos
                            resultados
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  {!tolCapIgnored && (
                    <>
                      <div className="flex items-center gap-1">
                        {CAP_PRICE_TOL_OPTIONS.map((opt) => (
                          <button
                            key={opt.label}
                            type="button"
                            onClick={() => {
                              setTolCapValue(opt.value);
                              setTolCapCustomStr("");
                            }}
                            className="flex-1 py-1.5 text-xs font-medium rounded-full transition-all"
                            style={
                              !tolCapCustomStr &&
                              Math.abs(tolCapValue - opt.value) < 0.001
                                ? { background: "#FFD900", color: "#1A1814" }
                                : { background: "#F0EEEB", color: "#78736D" }
                            }
                          >
                            {opt.label}
                          </button>
                        ))}
                        <div
                          className="flex items-center rounded-md shrink-0 h-7"
                          style={{
                            border: `1px solid ${
                              tolCapCustomStr &&
                              parseFloat(tolCapCustomStr) >= 100
                                ? "#EF4444"
                                : tolCapCustomStr &&
                                    !isNaN(parseFloat(tolCapCustomStr)) &&
                                    parseFloat(tolCapCustomStr) < 100
                                  ? "#FFD900"
                                  : "#E8E6E3"
                            }`,
                            background:
                              tolCapCustomStr &&
                              !isNaN(parseFloat(tolCapCustomStr)) &&
                              parseFloat(tolCapCustomStr) < 100
                                ? "rgba(255,217,0,0.10)"
                                : "white",
                          }}
                        >
                          <input
                            type="number"
                            min={0}
                            max={100}
                            step={1}
                            value={tolCapCustomStr}
                            onChange={(e) => {
                              const str = e.target.value;
                              setTolCapCustomStr(str);
                              const n = parseFloat(str);
                              if (!isNaN(n) && n >= 0 && n < 100) {
                                setTolCapValue(n / 100);
                              }
                            }}
                            placeholder="—"
                            className="w-14 px-1.5 py-0 text-xs outline-none text-center bg-transparent h-full"
                          />
                          <span
                            className="pr-2 text-xs"
                            style={{ color: "#9E9A94" }}
                          >
                            %
                          </span>
                        </div>
                      </div>
                      {tolCapCustomStr &&
                        parseFloat(tolCapCustomStr) >= 100 && (
                          <p
                            className="text-[11px] mt-1.5 flex items-center gap-1"
                            style={{ color: "#DC2626" }}
                          >
                            <AlertCircle className="w-3 h-3 shrink-0" />
                            Máximo 99% — use &ldquo;Ignorar&rdquo; para não
                            filtrar por capacidade
                          </p>
                        )}
                    </>
                  )}
                </div>

                {/* ── Distância — oculta no modo bairros (sem cálculo geo) ── */}
                {locationMode !== "neighborhoods" && (
                  <div
                    className="px-3 py-2.5"
                    style={{
                      background: "white",
                      borderBottom: "1px solid #F0EEEB",
                      opacity: tolDistIgnored ? 0.45 : 1,
                      transition: "opacity 0.15s",
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className="text-xs font-semibold"
                        style={{ color: "#43403D" }}
                      >
                        Distância
                      </span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <label className="flex items-center gap-1.5 cursor-pointer select-none">
                              <span
                                className="text-[11px]"
                                style={{ color: "#B8B4AE" }}
                              >
                                Ignorar
                              </span>
                              <Switch
                                checked={tolDistIgnored}
                                onCheckedChange={setTolDistIgnored}
                              />
                            </label>
                          </TooltipTrigger>
                          <TooltipContent
                            side="left"
                            className="max-w-[200px] text-center"
                          >
                            <p>
                              Quando ativado, a distância não elimina salas dos
                              resultados
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    {!tolDistIgnored &&
                      (() => {
                        const r = radiusKm ?? 5;
                        const extraKmVal = parseFloat(tolDistKm) || 0;
                        const totalKmStr =
                          tolDistKm !== ""
                            ? String(+(extraKmVal + r).toFixed(1))
                            : "";
                        const totalKmParsed = parseFloat(totalKmStr) || 0;
                        const distTolPct =
                          r > 0 && extraKmVal > 0
                            ? Math.round((extraKmVal / r) * 100)
                            : 0;
                        const distBelowRadius =
                          totalKmParsed > 0 && totalKmParsed < r;
                        return (
                          <>
                            <div className="flex items-center gap-2">
                              <div
                                className="flex items-center rounded-md shrink-0 h-7"
                                style={{
                                  border: `1px solid ${distBelowRadius ? "#EF4444" : "#E8E6E3"}`,
                                  background: "white",
                                }}
                              >
                                <input
                                  type="number"
                                  min={r}
                                  step={0.5}
                                  value={totalKmStr}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === "") {
                                      setTolDistKm("");
                                      return;
                                    }
                                    const total = parseFloat(val);
                                    if (!isNaN(total))
                                      setTolDistKm(
                                        String(
                                          +Math.max(0, total - r).toFixed(1),
                                        ),
                                      );
                                  }}
                                  placeholder={String(+(r * 1.25).toFixed(1))}
                                  className="w-16 px-2 py-0 text-xs outline-none text-center bg-transparent h-full"
                                />
                                <span
                                  className="pr-2.5 text-xs shrink-0"
                                  style={{ color: "#9E9A94" }}
                                >
                                  km
                                </span>
                              </div>
                              <span
                                className="text-xs"
                                style={{ color: "#78736D" }}
                              >
                                máximo total
                              </span>
                              {extraKmVal > 0 && (
                                <span
                                  className="ml-auto shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                                  style={{
                                    background: "#F0EEEB",
                                    color: "#78736D",
                                  }}
                                >
                                  +{extraKmVal.toFixed(1)}km · +{distTolPct}%
                                </span>
                              )}
                            </div>
                            <p
                              className="text-[11px] mt-1.5"
                              style={{ color: "#B8B4AE" }}
                            >
                              Raio base:{" "}
                              <span
                                className="font-medium"
                                style={{ color: "#9E9A94" }}
                              >
                                {r}km
                              </span>
                              {extraKmVal > 0 ? (
                                <>
                                  {" "}
                                  · salas até{" "}
                                  <span
                                    className="font-medium"
                                    style={{ color: "#9E9A94" }}
                                  >
                                    {totalKmStr}km
                                  </span>{" "}
                                  com pontuação reduzida
                                </>
                              ) : (
                                <> · sem tolerância extra configurada</>
                              )}
                            </p>
                            {distBelowRadius && (
                              <p
                                className="text-[11px] mt-0.5 flex items-center gap-1"
                                style={{ color: "#DC2626" }}
                              >
                                <AlertCircle className="w-3 h-3 shrink-0" />
                                Deve ser maior que o raio base ({r}km)
                              </p>
                            )}
                          </>
                        );
                      })()}
                  </div>
                )}

                {/* ── Preço ── */}
                <div
                  className="px-3 py-2.5"
                  style={{
                    background: "white",
                    opacity: tolPriceIgnored ? 0.45 : 1,
                    transition: "opacity 0.15s",
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className="text-xs font-semibold"
                      style={{ color: "#43403D" }}
                    >
                      Preço
                    </span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <label className="flex items-center gap-1.5 cursor-pointer select-none">
                            <span
                              className="text-[11px]"
                              style={{ color: "#B8B4AE" }}
                            >
                              Ignorar
                            </span>
                            <Switch
                              checked={tolPriceIgnored}
                              onCheckedChange={setTolPriceIgnored}
                            />
                          </label>
                        </TooltipTrigger>
                        <TooltipContent
                          side="left"
                          className="max-w-[200px] text-center"
                        >
                          <p>
                            Quando ativado, o preço não elimina salas dos
                            resultados
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  {!tolPriceIgnored && (
                    <>
                      <div className="flex items-center gap-1">
                        {CAP_PRICE_TOL_OPTIONS.map((opt) => (
                          <button
                            key={opt.label}
                            type="button"
                            onClick={() => {
                              setTolPriceValue(opt.value);
                              setTolPriceCustomStr("");
                            }}
                            className="flex-1 py-1.5 text-xs font-medium rounded-full transition-all"
                            style={
                              !tolPriceCustomStr &&
                              Math.abs(tolPriceValue - opt.value) < 0.001
                                ? { background: "#FFD900", color: "#1A1814" }
                                : { background: "#F0EEEB", color: "#78736D" }
                            }
                          >
                            {opt.label}
                          </button>
                        ))}
                        <div
                          className="flex items-center rounded-md shrink-0 h-7"
                          style={{
                            border: `1px solid ${
                              tolPriceCustomStr &&
                              parseFloat(tolPriceCustomStr) >= 100
                                ? "#EF4444"
                                : tolPriceCustomStr &&
                                    !isNaN(parseFloat(tolPriceCustomStr)) &&
                                    parseFloat(tolPriceCustomStr) < 100
                                  ? "#FFD900"
                                  : "#E8E6E3"
                            }`,
                            background:
                              tolPriceCustomStr &&
                              !isNaN(parseFloat(tolPriceCustomStr)) &&
                              parseFloat(tolPriceCustomStr) < 100
                                ? "rgba(255,217,0,0.10)"
                                : "white",
                          }}
                        >
                          <input
                            type="number"
                            min={0}
                            max={100}
                            step={1}
                            value={tolPriceCustomStr}
                            onChange={(e) => {
                              const str = e.target.value;
                              setTolPriceCustomStr(str);
                              const n = parseFloat(str);
                              if (!isNaN(n) && n >= 0 && n < 100) {
                                setTolPriceValue(n / 100);
                              }
                            }}
                            placeholder="—"
                            className="w-14 px-1.5 py-0 text-xs outline-none text-center bg-transparent h-full"
                          />
                          <span
                            className="pr-2 text-xs"
                            style={{ color: "#9E9A94" }}
                          >
                            %
                          </span>
                        </div>
                      </div>
                      {tolPriceCustomStr &&
                        parseFloat(tolPriceCustomStr) >= 100 && (
                          <p
                            className="text-[11px] mt-1.5 flex items-center gap-1"
                            style={{ color: "#DC2626" }}
                          >
                            <AlertCircle className="w-3 h-3 shrink-0" />
                            Máximo 99% — use &ldquo;Ignorar&rdquo; para não
                            filtrar por preço
                          </p>
                        )}
                    </>
                  )}
                </div>
              </div>
            </>
          </div>

          {/* Footer: apply + search */}
          <div
            className="px-5 py-4 flex gap-2"
            style={{ borderTop: "1px solid #E8E6E3" }}
          >
            <SheetClose asChild>
              <button
                type="button"
                className="flex-1 py-2.5 rounded-full text-sm font-medium transition-all"
                style={{ background: "#F0EEEB", color: "#43403D" }}
              >
                Fechar
              </button>
            </SheetClose>
            <SheetClose asChild>
              <button
                type="button"
                onClick={handleSearch}
                disabled={
                  rankStatus === "loading" ||
                  (locationMode === "location"
                    ? !hasValidLocationSelection
                    : !neighCity)
                }
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                style={{
                  background: "#1A1814",
                  color: "#FFD900",
                  letterSpacing: "0.03em",
                }}
              >
                <Search className="w-3.5 h-3.5" />
                Buscar
              </button>
            </SheetClose>
          </div>
        </SheetContent>
      </Sheet>

      {/* Tabs header — shown when there are results */}
      {displayRankStatus !== "loading" &&
        displayRankStatus !== "idle" &&
        displayRankStatus !== "error" &&
        (filteredDisplaySpaces.length > 0 ||
          displayCombinations.length > 0) && (
          <div className="flex flex-col gap-1.5">
            <div
              className="flex gap-0.5 p-0.5 rounded-full"
              style={{ background: "#F0EEEB", border: "1px solid #E8E6E3" }}
            >
              <button
                type="button"
                onClick={() => setActiveTab("recommended")}
                className="flex-1 text-[11px] font-medium py-1.5 rounded-full transition-all truncate"
                style={
                  activeTab === "recommended"
                    ? { background: "#1A1814", color: "#FFD900" }
                    : { background: "transparent", color: "#78736D" }
                }
              >
                Recomendados
                {matchedSpaces.length > 0 && (
                  <span className="ml-1 opacity-70">
                    ({matchedSpaces.length})
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("other")}
                className="flex-1 text-[11px] font-medium py-1.5 rounded-full transition-all truncate"
                style={
                  activeTab === "other"
                    ? { background: "#1A1814", color: "#FFD900" }
                    : { background: "transparent", color: "#78736D" }
                }
              >
                Outras opções
                {otherSpaces.length > 0 && (
                  <span className="ml-1 opacity-70">
                    ({otherSpaces.length})
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("combinations")}
                className="flex-1 text-[11px] font-medium py-1.5 rounded-full transition-all truncate"
                style={
                  activeTab === "combinations"
                    ? { background: "#1A1814", color: "#FFD900" }
                    : { background: "transparent", color: "#78736D" }
                }
              >
                Combinações
                {displayCombinations.length > 0 && (
                  <span className="ml-1 opacity-70">
                    ({displayCombinations.length})
                  </span>
                )}
              </button>
            </div>
            <div className="flex justify-end">
              <span className="text-xs" style={{ color: "#9E9A94" }}>
                {selectedSpaceIds.length} selecionado(s)
              </span>
            </div>
          </div>
        )}

      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto space-y-2 min-h-0"
      >
        {/* Salas da proposta — seção fixa no topo da lista em modo de edição */}
        {originalSpaces && originalSpaces.length > 0 && (
          <div
            className="rounded-2xl p-3 space-y-2"
            style={{ background: "#FAFAF8", border: "1px solid #E8E6E3" }}
          >
            <div className="flex items-center justify-between">
              <span
                className="text-[11px] font-semibold uppercase tracking-wide"
                style={{ color: "#78736D", letterSpacing: "0.07em" }}
              >
                Salas já presentes na proposta
              </span>
              <span className="text-xs" style={{ color: "#9E9A94" }}>
                {
                  originalSpaces.filter((s) => selectedSpaceIds.includes(s.id))
                    .length
                }
                /{originalSpaces.length} selecionada(s)
              </span>
            </div>
            <div className="space-y-2 overflow-y-auto max-h-[856px]">
              {originalSpaces.map((s) => {
                const effective = updatedOriginalSpaces.get(s.id) ?? s;
                const diff = spaceDiffs.get(s.id);
                return renderCard(effective, {
                  onDiffClick: diff
                    ? () => setDiffModalTarget(diff)
                    : undefined,
                });
              })}
            </div>
          </div>
        )}

        {originalSpaces && originalSpaces.length > 0 && (
          <div className="flex items-center gap-3 px-1">
            <div
              className="flex-1"
              style={{
                height: 6,
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='6'%3E%3Cpath d='M0,3 C4,0 8,6 16,3' fill='none' stroke='%23FFD900' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E\")",
                backgroundRepeat: "repeat-x",
                backgroundSize: "16px 6px",
                backgroundPosition: "0 center",
              }}
            />
            <span
              className="text-[10px] font-semibold tracking-wide uppercase shrink-0"
              style={{ color: "#78736D" }}
            >
              Resultados da busca
            </span>
            <div
              className="flex-1"
              style={{
                height: 6,
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='6'%3E%3Cpath d='M0,3 C4,0 8,6 16,3' fill='none' stroke='%23FFD900' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E\")",
                backgroundRepeat: "repeat-x",
                backgroundSize: "16px 6px",
                backgroundPosition: "0 center",
              }}
            />
          </div>
        )}

        {displayRankStatus === "loading" && <AISearchLoader />}
        {displayRankStatus === "loading" &&
          Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="rounded-xl border p-3 animate-pulse"
              style={{ borderColor: "#E8E6E3", background: "white" }}
            >
              <div className="flex items-center gap-2">
                <div
                  className="h-4 rounded flex-1"
                  style={{ background: "#F0EEEB" }}
                />
                <div
                  className="h-5 w-10 rounded-full"
                  style={{ background: "#F0EEEB" }}
                />
                <div
                  className="h-4 w-4 rounded"
                  style={{ background: "#F0EEEB" }}
                />
              </div>
              <div
                className="mt-1.5 h-3 w-2/3 rounded"
                style={{ background: "#F0EEEB" }}
              />
              <div
                className="mt-1.5 h-3 w-4/5 rounded"
                style={{ background: "#F0EEEB" }}
              />
              <div className="mt-2 flex items-center gap-3">
                <div
                  className="h-3 w-8 rounded"
                  style={{ background: "#F0EEEB" }}
                />
                <div
                  className="h-3 w-12 rounded"
                  style={{ background: "#F0EEEB" }}
                />
                <div
                  className="h-3 w-20 rounded"
                  style={{ background: "#F0EEEB" }}
                />
                <div
                  className="h-3 w-16 rounded"
                  style={{ background: "#F0EEEB" }}
                />
              </div>
              <div className="mt-2 flex justify-end">
                <div
                  className="h-3 w-16 rounded"
                  style={{ background: "#F0EEEB" }}
                />
              </div>
            </div>
          ))}
        {displayRankStatus === "error" && (
          <AISearchError onRetry={handleRetry} />
        )}
        {/* ── Tab: Recomendados ── */}
        {displayRankStatus !== "loading" &&
          displayRankStatus !== "error" &&
          activeTab === "recommended" && (
            <>
              {/* Salas adicionadas por ID — sempre no topo desta aba */}
              {idAddedSpaces.length > 0 && (
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 px-0.5">
                    <span
                      className="text-[10px] font-semibold uppercase tracking-wide"
                      style={{ color: "#78736D", letterSpacing: "0.07em" }}
                    >
                      Adicionadas por ID
                    </span>
                    <div
                      className="flex-1 h-px"
                      style={{ background: "#E8E6E3" }}
                    />
                  </div>
                  {idAddedSpaces.map((s) => renderCard(s))}
                </div>
              )}

              {displayRankStatus === "idle" && idAddedSpaces.length === 0 && (
                <div
                  className="flex flex-col items-center text-center py-8 px-4 rounded-2xl"
                  style={{
                    background: "#F7F5F2",
                    border: "1px dashed #E0DDD9",
                  }}
                >
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center mb-3"
                    style={{ background: "#F0EEEB" }}
                  >
                    <Search className="w-5 h-5" style={{ color: "#9E9A94" }} />
                  </div>
                  <p
                    className="text-sm font-semibold mb-1"
                    style={{ color: "#43403D" }}
                  >
                    Pronto para buscar
                  </p>
                  <p
                    className="text-xs leading-relaxed max-w-[220px]"
                    style={{ color: "#9E9A94" }}
                  >
                    Selecione uma localização e clique em Buscar para ver os
                    espaços disponíveis.
                  </p>
                </div>
              )}

              {displayRankStatus === "success" &&
                filteredDisplaySpaces.length === 0 && (
                  <>
                    <div
                      className="flex flex-col items-center text-center py-8 px-4 rounded-2xl"
                      style={{
                        background: "#F7F5F2",
                        border: "1px dashed #E0DDD9",
                      }}
                    >
                      <div
                        className="w-11 h-11 rounded-full flex items-center justify-center mb-3"
                        style={{ background: "#F0EEEB" }}
                      >
                        <Search
                          className="w-5 h-5"
                          style={{ color: "#9E9A94" }}
                        />
                      </div>
                      <p
                        className="text-sm font-semibold mb-1"
                        style={{ color: "#43403D" }}
                      >
                        Nenhum espaço encontrado
                      </p>
                      <p
                        className="text-xs leading-relaxed max-w-[240px]"
                        style={{ color: "#9E9A94" }}
                      >
                        {filteredOutdatedSpaces.length > 0
                          ? `Encontramos ${filteredOutdatedSpaces.length} espaço${filteredOutdatedSpaces.length !== 1 ? "s" : ""} com dados desatualizados que já atuaram nesta região.`
                          : "Tente ampliar o raio ou ajustar os filtros de tolerância."}
                      </p>
                    </div>
                    {filteredOutdatedSpaces.length > 0 && (
                      <>
                        <div className="flex items-center gap-2 py-1">
                          <div
                            className="flex-1 h-px"
                            style={{ background: "#E8E6E3" }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowOutdatedSpaces((v) => !v)}
                            className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-full font-medium transition-all"
                            style={{ background: "#FEF3C7", color: "#92400E" }}
                          >
                            <Clock className="w-3 h-3" />
                            {showOutdatedSpaces
                              ? "Ocultar desatualizados"
                              : `Ver ${filteredOutdatedSpaces.length} desatualizado${filteredOutdatedSpaces.length !== 1 ? "s" : ""}`}
                          </button>
                          <div
                            className="flex-1 h-px"
                            style={{ background: "#E8E6E3" }}
                          />
                        </div>
                        {showOutdatedSpaces && (
                          <div className="space-y-2 opacity-70">
                            {filteredOutdatedSpaces.map((s) => renderCard(s))}
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

              {displayRankStatus === "success" &&
                filteredDisplaySpaces.length > 0 &&
                matchedSpaces.length === 0 && (
                  <div
                    className="flex flex-col items-center text-center py-8 px-4 rounded-2xl"
                    style={{
                      background: "#FFFBEB",
                      border: "1px dashed #FDE68A",
                    }}
                  >
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center mb-3"
                      style={{ background: "#FEF3C7" }}
                    >
                      <CircleDashed
                        className="w-5 h-5"
                        style={{ color: "#92700C" }}
                      />
                    </div>
                    <p
                      className="text-sm font-semibold mb-1"
                      style={{ color: "#43403D" }}
                    >
                      Sem alta compatibilidade
                    </p>
                    <p
                      className="text-xs leading-relaxed max-w-[230px]"
                      style={{ color: "#78736D" }}
                    >
                      {otherSpaces.length === 1
                        ? "1 opção"
                        : `${otherSpaces.length} opções`}{" "}
                      com compatibilidade parcial
                      {displayCombinations.length > 0
                        ? " e combinações disponíveis."
                        : "."}
                    </p>
                    <div className="flex gap-2 mt-3">
                      {otherSpaces.length > 0 && (
                        <button
                          type="button"
                          className="text-xs font-medium px-4 py-1.5 rounded-full transition-all"
                          style={{ background: "#FFD900", color: "#1A1814" }}
                          onClick={() => setActiveTab("other")}
                        >
                          Ver outras opções
                        </button>
                      )}
                      {displayCombinations.length > 0 && (
                        <button
                          type="button"
                          className="text-xs font-medium px-4 py-1.5 rounded-full transition-all"
                          style={{ background: "#F0EEEB", color: "#43403D" }}
                          onClick={() => setActiveTab("combinations")}
                        >
                          Ver combinações
                        </button>
                      )}
                    </div>
                  </div>
                )}

              {matchedSpaces.length > 0 && (
                <>
                  {idAddedSpaces.length > 0 && (
                    <div className="flex items-center gap-1.5 px-0.5">
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wide"
                        style={{ color: "#78736D", letterSpacing: "0.07em" }}
                      >
                        Resultados da busca
                      </span>
                      <div
                        className="flex-1 h-px"
                        style={{ background: "#E8E6E3" }}
                      />
                    </div>
                  )}
                  {matchedSpaces.map((s) => renderCard(s))}
                </>
              )}
            </>
          )}

        {/* ── Tab: Outras opções ── */}
        {displayRankStatus !== "loading" &&
          displayRankStatus !== "error" &&
          activeTab === "other" && (
            <>
              {displayRankStatus === "idle" && (
                <div
                  className="flex flex-col items-center text-center py-8 px-4 rounded-2xl"
                  style={{
                    background: "#F7F5F2",
                    border: "1px dashed #E0DDD9",
                  }}
                >
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center mb-3"
                    style={{ background: "#F0EEEB" }}
                  >
                    <Search className="w-5 h-5" style={{ color: "#9E9A94" }} />
                  </div>
                  <p
                    className="text-sm font-semibold mb-1"
                    style={{ color: "#43403D" }}
                  >
                    Pronto para buscar
                  </p>
                  <p
                    className="text-xs leading-relaxed max-w-[220px]"
                    style={{ color: "#9E9A94" }}
                  >
                    Selecione uma localização e clique em Buscar para ver os
                    espaços disponíveis.
                  </p>
                </div>
              )}

              {displayRankStatus === "success" &&
                filteredDisplaySpaces.length === 0 && (
                  <div
                    className="flex flex-col items-center text-center py-8 px-4 rounded-2xl"
                    style={{
                      background: "#F7F5F2",
                      border: "1px dashed #E0DDD9",
                    }}
                  >
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center mb-3"
                      style={{ background: "#F0EEEB" }}
                    >
                      <Search
                        className="w-5 h-5"
                        style={{ color: "#9E9A94" }}
                      />
                    </div>
                    <p
                      className="text-sm font-semibold mb-1"
                      style={{ color: "#43403D" }}
                    >
                      Nenhum espaço encontrado
                    </p>
                    <p
                      className="text-xs leading-relaxed max-w-[230px]"
                      style={{ color: "#9E9A94" }}
                    >
                      Tente ampliar o raio ou ajustar os filtros de tolerância.
                    </p>
                    <button
                      type="button"
                      className="mt-3 text-xs font-medium px-4 py-1.5 rounded-full transition-all"
                      style={{ background: "#F0EEEB", color: "#43403D" }}
                      onClick={() => setActiveTab("recommended")}
                    >
                      Voltar para Recomendados
                    </button>
                  </div>
                )}

              {displayRankStatus === "success" &&
                filteredDisplaySpaces.length > 0 &&
                otherSpaces.length === 0 && (
                  <div
                    className="flex flex-col items-center text-center py-8 px-4 rounded-2xl"
                    style={{
                      background: "#F7F5F2",
                      border: "1px dashed #E0DDD9",
                    }}
                  >
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center mb-3"
                      style={{ background: "#F0EEEB" }}
                    >
                      <Search
                        className="w-5 h-5"
                        style={{ color: "#9E9A94" }}
                      />
                    </div>
                    <p
                      className="text-sm font-semibold mb-1"
                      style={{ color: "#43403D" }}
                    >
                      Sem opções parciais
                    </p>
                    <p
                      className="text-xs leading-relaxed max-w-[230px]"
                      style={{ color: "#9E9A94" }}
                    >
                      Todos os espaços encontrados têm alta compatibilidade.
                    </p>
                    <button
                      type="button"
                      className="mt-3 text-xs font-medium px-4 py-1.5 rounded-full transition-all"
                      style={{ background: "#F0EEEB", color: "#43403D" }}
                      onClick={() => setActiveTab("recommended")}
                    >
                      Ver recomendados
                    </button>
                  </div>
                )}

              {otherSpaces.length > 0 && (
                <div className="opacity-80 space-y-2">
                  {otherSpaces.map((s) => renderCard(s))}
                </div>
              )}
            </>
          )}

        {/* ── Tab: Combinações ── */}
        {displayRankStatus !== "loading" &&
          displayRankStatus !== "error" &&
          activeTab === "combinations" && (
            <>
              {displayRankStatus === "idle" && (
                <div
                  className="flex flex-col items-center text-center py-8 px-4 rounded-2xl"
                  style={{
                    background: "#F7F5F2",
                    border: "1px dashed #E0DDD9",
                  }}
                >
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center mb-3"
                    style={{ background: "#F0EEEB" }}
                  >
                    <LayoutGrid
                      className="w-5 h-5"
                      style={{ color: "#9E9A94" }}
                    />
                  </div>
                  <p
                    className="text-sm font-semibold mb-1"
                    style={{ color: "#43403D" }}
                  >
                    Combinações de salas
                  </p>
                  <p
                    className="text-xs leading-relaxed max-w-[220px]"
                    style={{ color: "#9E9A94" }}
                  >
                    Faça uma busca com capacidade mínima para ver sugestões de
                    salas combinadas.
                  </p>
                </div>
              )}

              {displayRankStatus === "success" &&
                filteredDisplaySpaces.length === 0 &&
                displayCombinations.length === 0 && (
                  <div
                    className="flex flex-col items-center text-center py-8 px-4 rounded-2xl"
                    style={{
                      background: "#F7F5F2",
                      border: "1px dashed #E0DDD9",
                    }}
                  >
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center mb-3"
                      style={{ background: "#F0EEEB" }}
                    >
                      <Search
                        className="w-5 h-5"
                        style={{ color: "#9E9A94" }}
                      />
                    </div>
                    <p
                      className="text-sm font-semibold mb-1"
                      style={{ color: "#43403D" }}
                    >
                      Nenhum espaço encontrado
                    </p>
                    <p
                      className="text-xs leading-relaxed max-w-[230px]"
                      style={{ color: "#9E9A94" }}
                    >
                      Tente ampliar o raio ou ajustar os filtros de tolerância.
                    </p>
                    <button
                      type="button"
                      className="mt-3 text-xs font-medium px-4 py-1.5 rounded-full transition-all"
                      style={{ background: "#F0EEEB", color: "#43403D" }}
                      onClick={() => setActiveTab("recommended")}
                    >
                      Voltar para Recomendados
                    </button>
                  </div>
                )}

              {displayRankStatus === "success" &&
                displayCombinations.length === 0 &&
                (filteredDisplaySpaces.length > 0 ||
                  idAddedSpaces.length > 0) && (
                  <div
                    className="flex flex-col items-center text-center py-8 px-4 rounded-2xl"
                    style={{
                      background: "#F7F5F2",
                      border: "1px dashed #E0DDD9",
                    }}
                  >
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center mb-3"
                      style={{ background: "#F0EEEB" }}
                    >
                      <LayoutGrid
                        className="w-5 h-5"
                        style={{ color: "#9E9A94" }}
                      />
                    </div>
                    <p
                      className="text-sm font-semibold mb-1"
                      style={{ color: "#43403D" }}
                    >
                      {capacityMin !== "" || capacityMax !== ""
                        ? "Nenhuma combinação encontrada"
                        : "Configure a capacidade"}
                    </p>
                    <p
                      className="text-xs leading-relaxed max-w-[230px]"
                      style={{ color: "#9E9A94" }}
                    >
                      {capacityMin !== "" || capacityMax !== ""
                        ? "Não foi possível combinar salas para atingir a capacidade solicitada nos resultados desta busca."
                        : "Informe uma capacidade mínima nos filtros para ver sugestões de combinações de salas."}
                    </p>
                  </div>
                )}

              {displayCombinations.length > 0 && (
                <>
                  {displayCombinations.map((combo: RoomCombination) => {
                    const allSelected = selectedComboIds.includes(
                      combo.combinationId,
                    );
                    const score = Math.round(combo.score * 100);
                    const isHighScore = score >= 80;

                    // Derive totals from full RankedSpace data (includes extra_rooms for combo-only rooms)
                    const allForLookup = [
                      ...filteredDisplaySpaces,
                      ...displayExtraSpaces,
                      ...idAddedSpaces,
                    ];
                    const fullRooms = combo.rooms.map((r) =>
                      allForLookup.find((s) => s.id === r.privateRoomId),
                    );
                    const totalCredits = fullRooms.reduce(
                      (sum, r) => sum + (r?.credits ?? 0),
                      0,
                    );
                    const totalSizeM2 = fullRooms.reduce(
                      (sum, r) => sum + (r?.sizeM2 ?? 0),
                      0,
                    );
                    const hasEstimatedPrice = fullRooms.some(
                      (r) =>
                        r?.source === "vip-office" &&
                        r?.origin === "integration",
                    );

                    return (
                      <div
                        key={combo.combinationId}
                        role="button"
                        tabIndex={0}
                        onClick={() => activateCombination(combo)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            activateCombination(combo);
                          }
                        }}
                        className="rounded-xl border p-3 transition-all cursor-pointer"
                        style={
                          activeCombinationIdRef.current === combo.combinationId
                            ? {
                                borderColor: "#FFD900",
                                background: "rgba(255,217,0,0.06)",
                                boxShadow: "0 0 0 1px rgba(255,217,0,0.4)",
                              }
                            : allSelected
                              ? {
                                  borderColor: "#FFD900",
                                  background: "rgba(255,217,0,0.06)",
                                  boxShadow: "0 0 0 1px rgba(255,217,0,0.4)",
                                }
                              : { borderColor: "#E8E6E3", background: "white" }
                        }
                      >
                        {/* Row 1 — name + score + checkbox */}
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm text-gray-900 truncate flex-1">
                            {combo.spaceName}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setComboScoreModal({
                                criteria: buildCombinationScoreCriteria(combo),
                                score,
                              });
                            }}
                            className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 transition-opacity hover:opacity-75"
                            style={
                              isHighScore
                                ? { background: "#FFF8D6", color: "#92700C" }
                                : { background: "#F0EEEB", color: "#78736D" }
                            }
                          >
                            {score}%
                            {isHighScore && <Sparkles className="w-3 h-3" />}
                          </button>
                          <Checkbox
                            checked={allSelected}
                            onCheckedChange={() => toggleCombination(combo)}
                            // Only stop the click from activating the card — the
                            // toggle is handled by onCheckedChange. Calling it here
                            // too would fire twice and cancel the selection.
                            onClick={(e) => e.stopPropagation()}
                            className="shrink-0"
                          />
                        </div>

                        {/* Row 2 — subtitle */}
                        <div className="mt-1 flex items-center gap-1">
                          <Link2
                            className="w-3 h-3 shrink-0"
                            style={{ color: "#9E9A94" }}
                          />
                          <p className="text-xs" style={{ color: "#9E9A94" }}>
                            Combinação de {combo.rooms.length} salas
                          </p>
                        </div>

                        {/* Row 3 — address */}
                        <div className="mt-1 flex items-center gap-1">
                          <MapPin
                            className="w-3 h-3 shrink-0"
                            style={{ color: "#9E9A94" }}
                          />
                          <span
                            className="text-xs truncate"
                            style={{ color: "#78736D" }}
                          >
                            {combo.neighborhood}
                            {combo.distanceKm > 0 &&
                              ` · ${combo.distanceKm.toFixed(1)}km`}
                          </span>
                        </div>

                        {/* Row 4 — stats */}
                        <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                          <span
                            className="inline-flex items-center gap-1 text-xs"
                            style={{ color: "#78736D" }}
                          >
                            <Users className="w-3 h-3" />
                            {combo.totalCapacity}
                          </span>
                          {totalSizeM2 > 0 && (
                            <span
                              className="inline-flex items-center gap-1 text-xs"
                              style={{ color: "#78736D" }}
                            >
                              <Maximize2 className="w-3 h-3" />
                              {totalSizeM2} m²
                            </span>
                          )}
                          {combo.totalMonthlyPrice > 0 && (
                            <span
                              className="inline-flex items-center gap-1 text-xs"
                              style={{ color: "#78736D" }}
                            >
                              <DollarSign className="w-3 h-3" />
                              {hasEstimatedPrice && "~"}R${" "}
                              {combo.totalMonthlyPrice.toLocaleString("pt-BR")}
                              /mês
                            </span>
                          )}
                          {totalCredits > 0 && (
                            <span
                              className="inline-flex items-center gap-1 text-xs"
                              style={{ color: "#78736D" }}
                            >
                              <Coins className="w-3 h-3" />
                              {totalCredits.toLocaleString("pt-BR")}
                            </span>
                          )}
                        </div>

                        {/* Row 5 — individual rooms, each with "Ver detalhes" */}
                        <div className="mt-2 space-y-1.5">
                          {combo.rooms.map((room) => {
                            // Rooms in combinations may not be in filteredDisplaySpaces
                            // (excluded individually). extra_rooms from the backend
                            // provides their full mapped data via buildResultItem.
                            const fullRoom =
                              filteredDisplaySpaces.find(
                                (s) => s.id === room.privateRoomId,
                              ) ??
                              idAddedSpaces.find(
                                (s) => s.id === room.privateRoomId,
                              ) ??
                              displayExtraSpaces.find(
                                (s) => s.id === room.privateRoomId,
                              ) ??
                              null;
                            const isRoomVip =
                              fullRoom?.source === "vip-office" &&
                              fullRoom?.origin === "integration";
                            return (
                              <div
                                key={room.privateRoomId}
                                className="flex items-center gap-2"
                              >
                                <span
                                  className="inline-flex items-center flex-1 min-w-0 text-[11px] px-2 py-0.5 rounded-full"
                                  style={{
                                    background: selectedSpaceIds.includes(
                                      room.privateRoomId,
                                    )
                                      ? "rgba(255,217,0,0.18)"
                                      : "#F0EEEB",
                                    color: "#43403D",
                                  }}
                                >
                                  <span className="truncate font-medium">
                                    {room.label}
                                  </span>
                                  <span
                                    className="ml-1.5 pl-1.5 shrink-0"
                                    style={{
                                      borderLeft: "1px solid rgba(0,0,0,0.12)",
                                      color: "#78736D",
                                    }}
                                  >
                                    <Users className="inline w-2.5 h-2.5 mr-0.5" />
                                    {room.capacity}
                                  </span>
                                  {room.monthlyPrice > 0 && (
                                    <span
                                      className="ml-1.5 pl-1.5 shrink-0 inline-flex items-center gap-0.5"
                                      style={{
                                        borderLeft:
                                          "1px solid rgba(0,0,0,0.12)",
                                        color: "#78736D",
                                      }}
                                    >
                                      <DollarSign className="inline w-2.5 h-2.5" />
                                      {isRoomVip && "~"}R${" "}
                                      {room.monthlyPrice.toLocaleString(
                                        "pt-BR",
                                      )}
                                      /mês
                                    </span>
                                  )}
                                </span>
                                {fullRoom && (
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-0.5 text-[11px] font-medium shrink-0 transition-opacity hover:opacity-70"
                                    style={{ color: "#43403D" }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDrawerSpace(fullRoom);
                                    }}
                                  >
                                    Ver detalhes
                                    <ChevronRight className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </>
          )}
      </div>

      <SpaceDetailDrawer
        open={!!drawerSpace}
        space={drawerSpace}
        onClose={() => setDrawerSpace(null)}
      />

      {diffModalTarget && (
        <SpaceDataDiffModal
          diff={diffModalTarget}
          onClose={() => setDiffModalTarget(null)}
          onAccept={handleAcceptDiff}
        />
      )}

      {comboScoreModal && (
        <ScoreDetailsModal
          scoreDetails={{
            scoreDetails: comboScoreModal.criteria,
            details: null,
          }}
          match={comboScoreModal.score}
          handleClose={() => setComboScoreModal(null)}
        />
      )}
    </div>
  );
}
